import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, updateRow } from '../../db/engine';
import { useApp } from '../../context/AppContext';
import { DollarSign, CheckCircle, Eye, X, RefreshCw, Building, FileText, Send, Edit2 } from 'lucide-react';
import ImportarEstadoCuenta from './ImportarEstadoCuenta';
import { fmtFecha, fmtFechaHora } from '../../utils/fecha';
import ClienteSelect from '../../components/ClienteSelect';
import PolizaSelect from '../../components/PolizaSelect';
import { loadXLSX } from '../../utils/xlsx';

import { fmtMoneda as fmt$ } from '../../utils/moneda';
import { marcaImpresion, getMarca, getAbrev } from '../../utils/marca';
import { imprimirHTML } from '../../utils/imprimir';
import { conceptoDeAjuste } from '../../db/comisiones';
const fmtPct = n => `${Number(n || 0).toFixed(1)}%`;

// Carga la librería XLSX desde CDN (una sola vez)

const ESTADO_INFO = {
  pendiente_aseg: { label:'Esp. Aseguradora', color:'var(--text-purple)', bg:'rgba(139, 92, 246, 0.1)', border:'rgba(139, 92, 246, 0.3)' },
  pendiente:      { label:'Pendiente Agente',  color:'var(--text-warning)', bg:'rgba(245, 158, 11, 0.1)', border:'rgba(245, 158, 11, 0.3)' },
  aprobado:       { label:'Aprobado',           color:'var(--sky)', bg:'rgba(var(--sky-rgb),0.1)', border:'rgba(var(--sky-rgb),0.3)' },
  pagado:         { label:'Pagado',             color:'var(--text-success)', bg:'rgba(34, 197, 94, 0.1)',  border:'rgba(34, 197, 94, 0.3)'  },
  anulada:        { label:'Anulada',            color:'var(--text-danger)', bg:'rgba(239, 68, 68, 0.1)',  border:'rgba(239, 68, 68, 0.3)'  },
};

// Orden de avance de la gestión (para el merge inteligente al anular duplicadas)
const ESTADO_RANK = { pendiente_aseg:0, pendiente:1, aprobado:2, pagado:3 };

// Redondeo a 2 decimales (dinero)
const r2 = n => Math.round((Number(n) || 0) * 100) / 100;

export default function Comisiones() {
  const { canEdit, user, esAgenteRestringido } = useApp();
  const abrev = getAbrev();
  const agScopeId = esAgenteRestringido ? (user?.agente_id ?? -1) : null;
  // El agente solo ve desde lo COBRADO de la aseguradora; lo pendiente lo gestiona la operación.
  const [tab, setTab]                 = useState(esAgenteRestringido ? 'aseg' : 'pendientes'); // 'pendientes' | 'aseg' | 'agentes'
  const [rows, setRows]               = useState([]);
  const [filterPoliza, setFilterPoliza] = useState('');
  const [filterEstado, setFilterEstado] = useState('pendiente_aseg');
  const [sortCol, setSortCol]         = useState('');
  const [sortDir, setSortDir]         = useState('desc');
  const [modalAseg, setModalAseg]     = useState(null);
  const [fechaAseg, setFechaAseg]     = useState('');
  const [numRemision, setNumRemision] = useState('');
  const [filterAgente, setFilterAgente] = useState('');
  const [filterAseguradora, setFilterAseguradora] = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [aseguradoras, setAseguradoras] = useState([]);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [agentes, setAgentes]         = useState([]);
  const [referidoMap, setReferidoMap] = useState({}); // { referido_id: nombre }
  const [totales, setTotales]         = useState({ esp_aseg:0, ru_esp_aseg:0, pendiente_agente:0, ru_total:0, esp_total:0 });
  const [modalEstadoCuenta, setModalEstadoCuenta] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const navigate = useNavigate();
  // Abre la vista COMPLETA de la póliza navegando al módulo Pólizas (misma vista que "Ver póliza").
  const verPolizaCompleta = (polizaId) => {
    if (!polizaId) return;
    sessionStorage.setItem('openPolizaId', String(polizaId));
    sessionStorage.setItem('backFrom', 'comisiones');
    navigate('/polizas');
  };
  const [detalle, setDetalle]         = useState(null);   // comision seleccionada
  const [detalleData, setDetalleData] = useState(null);   // { poliza, pagos, otrasComisiones }
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [notaEdit, setNotaEdit]       = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [notaAseg, setNotaAseg]       = useState('');
  const [modalLiquidar, setModalLiquidar] = useState(null); // { agente_id, agente_nombre, items[] }
  const [fechaTransf, setFechaTransf] = useState('');
  const [numTransf, setNumTransf]     = useState('');
  const [notaLiquidacion, setNotaLiquidacion] = useState('');
  const [liquidando, setLiquidando]   = useState(false);
  const [bolsaRU, setBolsaRU]         = useState({ total:0, count:0, items:[] });
  const [modalCierreRU, setModalCierreRU] = useState(false);
  const [fechaCierreRU, setFechaCierreRU] = useState('');
  const [cerrandoRU, setCerrandoRU]   = useState(false);
  const [modalReverso, setModalReverso] = useState(null); // { cierre_ts, total, count }
  const [revirtiendo, setRevirtiendo] = useState(false);
  // Anular comisión (creada por error / duplicada)
  const [modalAnular, setModalAnular] = useState(null);   // comision a anular
  const [anularTipo, setAnularTipo]   = useState('duplicada'); // 'duplicada' | 'error'
  const [anularDupId, setAnularDupId] = useState('');     // id de la comisión original (caso duplicada)
  const [anularTexto, setAnularTexto] = useState('');
  const [anulando, setAnulando]       = useState(false);
  // Pago recibido de aseguradora en bloque (con montos parciales por póliza)
  const [modalPagoAsegMasivo, setModalPagoAsegMasivo] = useState(false);
  const [pagoAsegSel, setPagoAsegSel]     = useState({});   // { [comision_id]: { sel, recibido } }
  const [pagoAsegFecha, setPagoAsegFecha] = useState('');
  const [pagoAsegComp, setPagoAsegComp]   = useState('');
  const [guardandoPagoAseg, setGuardandoPagoAseg] = useState(false);
  // Corregir un pago de aseguradora YA registrado (fecha / comprobante), SOLO master.
  // El monto recibido no se toca: para eso está anular. Ver abrirCorregirPagoAseg.
  const [modalCorregirAseg, setModalCorregirAseg] = useState(null); // comisión base
  const [corregirForm, setCorregirForm] = useState({ fecha:'', comprobante:'', nota:'', lote:false });
  const [corregirLote, setCorregirLote] = useState([]);   // otras comisiones del MISMO pago (misma fecha + comprobante)
  const [corrigiendo, setCorrigiendo] = useState(false);
  // Revertir un pago de aseguradora registrado por error: devuelve la comisión a
  // "Esp. Aseguradora" y limpia fecha/comprobante/monto recibido. Solo mientras NO
  // se haya aprobado ni pagado al agente. Ver abrirRevertirPagoAseg.
  const [modalRevertirAseg, setModalRevertirAseg] = useState(null); // comisión base
  const [revertirForm, setRevertirForm] = useState({ nota:'', lote:false });
  const [revertirLote, setRevertirLote] = useState([]);   // otras comisiones del MISMO pago, aún reversibles
  const [revirtiendoAseg, setRevirtiendoAseg] = useState(false);

  const load = useCallback(async () => {
    let q = supabase.from('comisiones')
      .select('*, polizas(numero, prima, comision_ramo, impuesto, aseguradora_id, cliente_id, clientes(nombre), ramos(nombre), aseguradoras(nombre)), agentes(nombre), cobros_recibidos(id, monto, fecha, donde_pago, forma_pago, num_recibo_aseg, notas), pagos(id, monto, fecha_pago, donde_pago, forma_pago_real, cuota_num, estado)');

    if (tab === 'pendientes') {
      if (filterEstado) q = q.eq('estado', filterEstado);
      else q = q.in('estado', ['pendiente_aseg', 'pendiente']);
      if (filterAgente) q = q.eq('agente_id', filterAgente);
      q = q.order('creado_en', { ascending: false });
    } else if (tab === 'bolsa') {
      q = q.eq('estado', 'aprobado');
      if (filterAgente) q = q.eq('agente_id', filterAgente);
      q = q.order('agente_id', { ascending: true });
    } else if (tab === 'aseg') {
      q = q.eq('estado', 'pendiente');
      if (filterAgente) q = q.eq('agente_id', filterAgente);
      q = q.order('fecha_recibio_comision', { ascending: false });
    } else if (tab === 'agentes') {
      q = q.eq('estado', 'pagado');
      if (filterAgente) q = q.eq('agente_id', filterAgente);
      q = q.order('pagado_en', { ascending: false });
    } else if (tab === 'ru_cobrado') {
      q = q.not('ru_liquidado_en', 'is', null).neq('estado', 'anulada');
      if (filterAgente) q = q.eq('agente_id', filterAgente);
      q = q.order('ru_liquidado_en', { ascending: false });
    }
    // Agente restringido: solo sus comisiones
    if (agScopeId != null) q = q.eq('agente_id', agScopeId);

    const { data } = await q;
    let arr = (data || []).map(c => ({
      ...c,
      poliza_num:    c.polizas?.numero,
      poliza_prima:  c.polizas?.prima,
      poliza_ramo:   c.polizas?.ramos?.nombre,
      comision_ramo: c.polizas?.comision_ramo,
      impuesto:      c.polizas?.impuesto ?? 5,
      aseguradora:   c.polizas?.aseguradoras?.nombre,
      aseguradora_id: c.polizas?.aseguradora_id,
      cliente_id:    c.polizas?.cliente_id,
      cliente:       c.polizas?.clientes?.nombre,
      agente_nombre: c.agentes?.nombre,
      pago_info:     c.cobros_recibidos || c.pagos,
      pago_info_tipo: c.cobros_recibidos ? 'cobro' : 'pago',
      // Fecha en que el cliente pagó. Si el documento/cobro no la trae, se usa
      // como fallback la fecha en que la aseguradora pagó a RU (acuerdo con Ricardo).
      fecha_pago_cliente: c.cobros_recibidos?.fecha || c.pagos?.fecha_pago || c.fecha_recibio_comision || null,
      fecha_cobro_agente: c.pagado_en?.slice(0,10) || c.fecha_liquidacion || null,
      fecha_ru_cobrado: c.ru_liquidado_en?.slice(0,10) || null,
    }));
    // Nombres de referidos para las filas de comisión de referido (agente_id null, referido_id lleno)
    const refIds = [...new Set(arr.map(c => c.referido_id).filter(Boolean))];
    let refMap = {};
    if (refIds.length) {
      try {
        const { data: refs } = await supabase.from('referidos').select('id, nombre').in('id', refIds);
        (refs || []).forEach(r => { refMap[r.id] = r.nombre; });
      } catch { refMap = {}; }
    }
    setReferidoMap(refMap);
    arr = arr.map(c => ({
      ...c,
      referido_nombre: c.referido_id ? (refMap[c.referido_id] || `Referido #${c.referido_id}`) : null,
    }));

    // Filtros por selección (PolizaSelect/ClienteSelect): la lista solo se filtra al seleccionar.
    if (filterPoliza) arr = arr.filter(c => String(c.poliza_id) === String(filterPoliza));
    if (filterAseguradora) arr = arr.filter(c => String(c.aseguradora_id) === String(filterAseguradora));
    if (filterCliente) arr = arr.filter(c => String(c.cliente_id) === String(filterCliente));
    setRows(arr);

    // Totales separados por estado. Las tarjetas de resumen respetan los MISMOS
    // filtros que la tabla (agente, aseguradora, cliente, póliza) para que al
    // filtrar por ej. ASSA muestren los totales de ASSA.
    // OJO: `comisiones` NO tiene aseguradora_id ni cliente_id — esos datos son de
    // la PÓLIZA (arriba se aplanan en memoria para la tabla). Por eso, cuando se
    // filtra por aseguradora o cliente hay que hacerlo a través de la relación con
    // un join interno; consultarlos directo sobre `comisiones` rompe la query y
    // deja las tarjetas en cero.
    const porPoliza = !!(filterAseguradora || filterCliente);
    let qtBase = supabase.from('comisiones').select(
      porPoliza
        ? 'estado, monto_agente, monto_ru, monto_recibido_aseg, polizas!inner(aseguradora_id, cliente_id)'
        : 'estado, monto_agente, monto_ru, monto_recibido_aseg'
    );
    if (filterAgente) qtBase = qtBase.eq('agente_id', filterAgente);
    if (agScopeId != null) qtBase = qtBase.eq('agente_id', agScopeId);
    if (filterAseguradora) qtBase = qtBase.eq('polizas.aseguradora_id', filterAseguradora);
    if (filterCliente) qtBase = qtBase.eq('polizas.cliente_id', filterCliente);
    if (filterPoliza) qtBase = qtBase.eq('poliza_id', filterPoliza);   // este sí está en comisiones
    const { data: allPend } = await qtBase.in('estado', ['pendiente_aseg', 'pendiente', 'aprobado']);
    const espAseg  = (allPend||[]).filter(r=>r.estado==='pendiente_aseg').reduce((s,r)=>s+Number(r.monto_agente||0),0);
    const ruEspAseg = (allPend||[]).filter(r=>r.estado==='pendiente_aseg').reduce((s,r)=>s+Number(r.monto_ru||0),0);
    const pendAg   = (allPend||[]).filter(r=>['pendiente','aprobado'].includes(r.estado)).reduce((s,r)=>s+Number(r.monto_agente||0),0);
    const ruTotal  = (allPend||[]).filter(r=>['pendiente','aprobado'].includes(r.estado)).reduce((s,r)=>s+Number(r.monto_ru||0),0);
    // Total que REALMENTE falta cobrarle a las aseguradoras: descuenta lo ya
    // recibido de forma parcial (si pagaron 75 de 80, quedan 5 por cobrar).
    const espTotal = (allPend||[]).filter(r=>r.estado==='pendiente_aseg')
      .reduce((s,r)=>s + Number(r.monto_agente||0) + Number(r.monto_ru||0) - Number(r.monto_recibido_aseg||0), 0);
    setTotales({ esp_aseg: espAseg, ru_esp_aseg: ruEspAseg, pendiente_agente: pendAg, ru_total: ruTotal, esp_total: espTotal });

    // Bolsa de RU: porción de RU de comisiones del flujo (aprobado_en lleno) aún no cerradas por RU.
    // aprobado_en distingue el flujo actual de las históricas (que no lo tienen).
    let ruQ = supabase.from('comisiones')
      .select('id, monto_ru, estado, cobro_id, cobros_recibidos(fecha, monto), polizas(numero, clientes(nombre)), agentes(nombre)')
      .not('aprobado_en', 'is', null)
      .is('ru_liquidado_en', null)
      .neq('estado', 'anulada');
    if (agScopeId != null) ruQ = ruQ.eq('agente_id', agScopeId);
    const { data: ruPend } = await ruQ.order('monto_ru', { ascending: false });
    setBolsaRU({
      total: (ruPend||[]).reduce((s,r)=>s+Number(r.monto_ru||0),0),
      count: (ruPend||[]).length,
      items: (ruPend||[]).map(r => ({
        id: r.id,
        monto_ru: r.monto_ru,
        estado: r.estado,
        poliza_num: r.polizas?.numero || '—',
        cliente: r.polizas?.clientes?.nombre || '—',
        agente: r.agentes?.nombre || 'Sin agente',
        cobro_fecha: r.cobros_recibidos?.fecha || null,
        cobro_monto: r.cobros_recibidos?.monto ?? null,
      })),
    });
  }, [filterPoliza, filterEstado, filterAgente, filterAseguradora, filterCliente, tab, agScopeId]);

  useEffect(() => { load(); }, [load]);

  // Defensivo: un agente nunca debe quedarse en una pestaña que no le corresponde.
  useEffect(() => {
    if (esAgenteRestringido && (tab === 'pendientes' || tab === 'ru_cobrado')) setTab('aseg');
  }, [esAgenteRestringido, tab]);

  // Cargar lista de agentes para el filtro
  useEffect(() => {
    supabase.from('agentes').select('id, nombre').eq('activo', true).order('nombre')
      .then(({ data }) => setAgentes(data || []));
    supabase.from('aseguradoras').select('id, nombre').order('nombre')
      .then(({ data }) => setAseguradoras(data || []));
  }, []);

  const registrarPagoAseguradora = async () => {
    if (!fechaAseg) { alert('La fecha de pago es requerida.'); return; }
    try {
      await updateRow('comisiones', modalAseg.id, {
        estado: 'pendiente',
        fecha_recibio_comision: fechaAseg,
        num_comprobante: numRemision || modalAseg.num_comprobante || null,
        // Se anexa (no se pisa): la nota previa puede ser el porqué de un ajuste.
        notas: [modalAseg.notas, notaAseg.trim()].filter(Boolean).join('\n') || null,
      });
      setModalAseg(null); setFechaAseg(''); setNumRemision(''); setNotaAseg('');
      await load();
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  // ── Pago recibido de aseguradora (varias comisiones de una) ──────────────
  // Gynnela filtra la aseguradora, marca las comisiones que recibió y edita el
  // monto real por póliza. Si la aseguradora pagó de MENOS, el saldo (esperado −
  // recibido) queda pendiente de la aseguradora para darle seguimiento: la
  // comisión NO pasa a "Cobrado de Aseguradoras" hasta completarse.
  const recibidoDe = (r) => Number(r.monto_recibido_aseg || 0);
  const esperadoDe = (r) => r2(Number(r.monto_agente || 0) + Number(r.monto_ru || 0));
  const saldoAsegDe = (r) => r2(esperadoDe(r) - recibidoDe(r));

  const abrirPagoAsegMasivo = () => {
    const items = sortedRows.filter(r => r.estado === 'pendiente_aseg');
    if (!items.length) { alert('No hay comisiones esperando a la aseguradora con los filtros actuales.'); return; }
    const sel = {};
    items.forEach(r => { sel[r.id] = { sel: true, recibido: String(saldoAsegDe(r).toFixed(2)), comp: '' }; });
    setPagoAsegSel(sel);
    setPagoAsegFecha(new Date().toISOString().slice(0, 10));
    setPagoAsegComp('');
    setModalPagoAsegMasivo(true);
  };

  const guardarPagoAsegMasivo = async () => {
    if (!pagoAsegFecha) { alert('Pon la fecha en que la aseguradora pagó.'); return; }
    const items = sortedRows.filter(r => r.estado === 'pendiente_aseg' && pagoAsegSel[r.id]?.sel);
    if (!items.length) { alert('Selecciona al menos una comisión.'); return; }
    setGuardandoPagoAseg(true);
    let ok = 0, parciales = 0; const errores = [];
    for (const r of items) {
      try {
        const monto = Number(pagoAsegSel[r.id]?.recibido);
        if (!(monto > 0)) { errores.push(`${r.polizas?.numero || r.poliza_id}: monto inválido`); continue; }
        const acumulado = r2(recibidoDe(r) + monto);
        const completo = acumulado >= esperadoDe(r) - 0.005;
        // Comprobante: el de la fila si la aseguradora da uno por póliza; si no,
        // el general de arriba (cuando todo el pago viene con un solo número).
        const comp = (pagoAsegSel[r.id]?.comp || '').trim() || pagoAsegComp.trim();
        await updateRow('comisiones', r.id, {
          monto_recibido_aseg: acumulado,
          ...(completo ? { estado: 'pendiente', fecha_recibio_comision: pagoAsegFecha } : {}),
          ...(comp ? { num_comprobante: comp } : {}),
        });
        ok++; if (!completo) parciales++;
      } catch (e) { errores.push(`${r.polizas?.numero || r.poliza_id}: ${e.message || e}`); }
    }
    setGuardandoPagoAseg(false);
    setModalPagoAsegMasivo(false);
    await load();
    alert(`✅ ${ok} comisión(es) actualizadas.` +
      (parciales ? ` ⚠️ ${parciales} quedaron con saldo pendiente de la aseguradora (siguen en "Esp. Aseguradora" para seguimiento).` : '') +
      (errores.length ? `\n\nErrores: ${errores.join('; ')}` : ''));
  };

  const abrirLiquidarAgente = (agenteId) => {
    const items = sortedRows.filter(r => r.agente_id === agenteId && !r.referido_id && r.estado === 'aprobado');
    if (!items.length) return;
    setModalLiquidar({ agente_id: agenteId, agente_nombre: items[0].agente_nombre, items });
    setFechaTransf(new Date().toISOString().slice(0,10));
    setNumTransf('');
    setNotaLiquidacion('');
  };

  // Liquidar a un DESTINATARIO (agente o referido) — mismo flujo/modal.
  const abrirLiquidarPayee = (p) => {
    const items = sortedRows.filter(r => r.estado === 'aprobado' &&
      (p.esRef ? r.referido_id === p.id : (r.agente_id === p.id && !r.referido_id)));
    if (!items.length) return;
    setModalLiquidar({ agente_id: p.esRef ? null : p.id, referido_id: p.esRef ? p.id : null, agente_nombre: p.nombre, items });
    setFechaTransf(new Date().toISOString().slice(0,10));
    setNumTransf('');
    setNotaLiquidacion('');
  };

  const confirmarLiquidacion = async () => {
    if (!fechaTransf) { alert('La fecha de transferencia es requerida.'); return; }
    if (!numTransf.trim()) { alert('El número de transferencia es requerido.'); return; }
    setLiquidando(true);
    try {
      const nota = `Transferencia #${numTransf.trim()} · ${fechaTransf}`
        + (notaLiquidacion.trim() ? ` · ${notaLiquidacion.trim()}` : '');
      for (const c of modalLiquidar.items) {
        await updateRow('comisiones', c.id, {
          estado: 'pagado',
          pagado_en: new Date(fechaTransf + 'T12:00:00').toISOString(),
          // Se ANEXA: pisar la nota borraba el porqué de un ajuste (y la marca que lo hace
          // idempotente) y la traza de una corrección de pago de aseguradora.
          notas: c.notas ? `${c.notas}\n${nota}` : nota,
        });
      }
      setModalLiquidar(null); setFechaTransf(''); setNumTransf(''); setNotaLiquidacion('');
      await load();
      alert(`✅ ${modalLiquidar.items.length} comisión(es) marcadas como pagadas. Transferencia #${numTransf}`);
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setLiquidando(false); }
  };

  // Cerrar TODA la bolsa de RU de una vez: marca ru_liquidado_en en las comisiones
  // del flujo (aprobado_en lleno) aún no cerradas por RU.
  const cerrarBolsaRU = async () => {
    if (!fechaCierreRU) { alert('Indica la fecha del cierre.'); return; }
    setCerrandoRU(true);
    try {
      // Fecha del cierre elegida por el usuario + hora actual (para que cada cierre
      // tenga un timestamp único y no se mezcle con otro del mismo día en "RU Cobrado").
      const now = new Date();
      const hhmmss = now.toTimeString().slice(0, 8);
      const ts = new Date(`${fechaCierreRU}T${hhmmss}`).toISOString();
      const { error } = await supabase.from('comisiones')
        .update({ ru_liquidado_en: ts })
        .not('aprobado_en', 'is', null)
        .is('ru_liquidado_en', null);
      if (error) throw error;
      setModalCierreRU(false);
      await load();
      alert(`✅ Bolsa de ${abrev} cerrada. La porción de ${abrev} quedó registrada como cobrada.`);
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setCerrandoRU(false); }
  };

  // Revertir un cierre de RU completo (solo es_master): devuelve las comisiones de
  // ese cierre a la bolsa (ru_liquidado_en = NULL) y deja registro de auditoría.
  const revertirCierreRU = async () => {
    if (!modalReverso) return;
    setRevirtiendo(true);
    try {
      // 1) Registro de auditoría (quién y cuándo revirtió)
      const { error: errLog } = await supabase.from('ru_cierre_reversos').insert({
        cierre_ts: modalReverso.cierre_ts,
        total_ru: modalReverso.total,
        num_comisiones: modalReverso.count,
        revertido_por: user?.nombre || user?.email || '—',
        revertido_por_id: user?.id || null,
      });
      if (errLog) throw errLog;
      // 2) Devolver las comisiones de ese cierre a la bolsa
      const { error } = await supabase.from('comisiones')
        .update({ ru_liquidado_en: null })
        .eq('ru_liquidado_en', modalReverso.cierre_ts);
      if (error) throw error;
      setModalReverso(null);
      await load();
      alert(`✅ Cierre de ${abrev} revertido. Las comisiones volvieron a la bolsa de "Por Liquidar".`);
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setRevirtiendo(false); }
  };

  const imprimirLiquidacion = (items, agente, fecha, numTrans) => {
    const M = marcaImpresion();
    const total = items.reduce((s, c) => s + Number(c.monto_agente || 0), 0);
    const fmtN = n => `$${Number(n||0).toLocaleString('es-PA',{minimumFractionDigits:2})}`;
    const rows = items.map(c => {
      const primaCon = Number(c.prima || 0);
      const imp = Number(c.impuesto ?? 5);
      const primaSin = c.prima_base != null ? Number(c.prima_base) : Math.round(primaCon / (1 + imp/100) * 100) / 100;
      const comRamo = Number(c.comision_ramo || 0);
      const comRU = Math.round(primaSin * comRamo / 100 * 100) / 100;
      const pctAg = Number(c.porcentaje_agente || 0);
      // Un ajuste (negativo por anulación, diferencia de %, reaplicación) dice a qué se refiere.
      const concepto = conceptoDeAjuste(c.notas).replace(/&/g, '&amp;').replace(/</g, '&lt;');
      return `<tr>
        <td>${c.aseguradora || '—'}</td>
        <td>${c.poliza_num || '—'}${c.es_sobrecomision ? ' <b style="color:#b8860b">(Sobrecom.)</b>' : ''}${concepto ? `<div style="font-size:9px;color:#8a4b00;margin-top:2px;max-width:230px"><b>AJUSTE:</b> ${concepto}</div>` : ''}</td>
        <td>${c.cliente || '—'}</td>
        <td>${fmtFecha(c.fecha_pago_cliente) || '—'}</td>
        <td style="text-align:right">${fmtN(primaCon)}</td>
        <td style="text-align:right">${fmtN(primaSin)}</td>
        <td style="text-align:center">${comRamo ? comRamo+'%' : '—'}</td>
        <td style="text-align:right">${fmtN(comRU)}</td>
        <td style="text-align:center">${pctAg ? pctAg+'%' : '—'}</td>
        <td style="text-align:right;font-weight:700">${fmtN(c.monto_agente)}</td>
        <td>${fmtFecha(fecha)}</td>
        <td>${numTrans || '—'}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>Liquidación — ${agente}</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:20px}
        h2{font-size:15px;margin-bottom:4px}
        .sub{font-size:11px;color:#555;margin-bottom:16px}
        .brand{display:flex;align-items:center;gap:10px;margin-bottom:14px;border-bottom:2px solid ${M.colores.steel};padding-bottom:10px}
        .brand-name{font-size:16px;font-weight:800;color:${M.colores.steel};letter-spacing:1px}
        .brand-tag{font-size:10px;color:${M.colores.sky}}
        table{width:100%;border-collapse:collapse;font-size:10px}
        th{background:${M.colores.steel};color:#fff;padding:6px 4px;text-align:left;white-space:nowrap}
        td{padding:5px 4px;border-bottom:1px solid #e0e0e0;vertical-align:top}
        tr:nth-child(even) td{background:#f7f9fb}
        .total-row td{font-weight:700;border-top:2px solid ${M.colores.steel};background:#e8f0f8}
        @media print{body{margin:10px}button{display:none}}
      </style></head><body>
      <div class="brand">${M.logo}<div><div class="brand-name" style="text-transform:uppercase">${M.nombre}</div><div class="brand-tag">${M.tagline}</div></div></div>
      <h2>Liquidación de Comisiones — ${agente}</h2>
      <div class="sub">Transferencia #${numTrans} &nbsp;·&nbsp; Fecha: ${fmtFecha(fecha)}</div>
      <table>
        <thead><tr>
          <th>Aseguradora</th><th>Póliza</th><th>Cliente</th><th>F. Pago Cliente</th>
          <th>Prima c/imp</th><th>Prima s/imp</th><th>% Com</th><th>$ Com ${abrev}</th>
          <th>% Ag</th><th>$ Agente</th><th>F. Liquidación</th><th>No. Transf.</th>
        </tr></thead>
        <tbody>${rows}
          <tr class="total-row">
            <td colspan="9" style="text-align:right;padding-right:8px">TOTAL A PAGAR AL AGENTE</td>
            <td style="text-align:right">${fmtN(total)}</td>
            <td colspan="2"></td>
          </tr>
        </tbody>
      </table>
    </body></html>`;
    // Imprimir vía iframe oculto y aislado (utils/imprimir.js) — no depende de ventanas emergentes
    imprimirHTML(html, (m) => alert(m.replace('No se pudo imprimir', 'No se pudo generar el PDF')));
  };

  const exportarLiquidacionExcel = async (items, agente, fecha, numTrans) => {
    try {
      const M = marcaImpresion();
      const XLSX = await loadXLSX();
      const fmtN = n => Math.round((Number(n || 0)) * 100) / 100;
      const aoa = [
        [`${M.nombre} — Liquidación de Comisiones`],
        ['Agente: ' + agente],
        ['Transferencia #' + (numTrans || '') + '   ·   Fecha: ' + (fmtFecha(fecha) || '')],
        [],
        ['Aseguradora','Póliza','Cliente','F. Pago Cliente','Prima c/imp','Prima s/imp','% Com',`$ Com ${abrev}`,'% Ag','$ Agente','F. Liquidación','No. Transf.','Concepto del ajuste'],
      ];
      let total = 0;
      for (const c of items) {
        const primaCon = Number(c.prima || 0);
        const imp = Number(c.impuesto ?? 5);
        const primaSin = c.prima_base != null ? Number(c.prima_base) : Math.round(primaCon / (1 + imp/100) * 100) / 100;
        const comRamo = Number(c.comision_ramo || 0);
        const comRU = Math.round(primaSin * comRamo / 100 * 100) / 100;
        const pctAg = Number(c.porcentaje_agente || 0);
        total += Number(c.monto_agente || 0);
        aoa.push([
          c.aseguradora || '', (c.poliza_num || '') + (c.es_sobrecomision ? ' (Sobrecom.)' : ''), c.cliente || '', fmtFecha(c.fecha_pago_cliente) || '',
          fmtN(primaCon), fmtN(primaSin), comRamo ? comRamo + '%' : '', fmtN(comRU),
          pctAg ? pctAg + '%' : '', fmtN(c.monto_agente), fmtFecha(fecha) || '', numTrans || '',
          conceptoDeAjuste(c.notas),
        ]);
      }
      aoa.push([]);
      aoa.push(['', '', '', '', '', '', '', '', 'TOTAL', fmtN(total), '', '']);
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [{wch:18},{wch:16},{wch:26},{wch:14},{wch:11},{wch:11},{wch:7},{wch:10},{wch:7},{wch:11},{wch:12},{wch:14},{wch:70}];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Liquidación');
      const safe = String(agente || 'agente').replace(/[^a-zA-Z0-9]/g, '_');
      XLSX.writeFile(wb, `Liquidacion_${safe}_${fecha || ''}.xlsx`);
    } catch (e) {
      alert('No se pudo generar el Excel: ' + (e.message || ''));
    }
  };

  // Exportar a Excel la lista de la PESTAÑA activa, con sus columnas y los filtros aplicados.
  const exportarComisionesExcel = async () => {
    try {
      const M = marcaImpresion();
      const XLSX = await loadXLSX();
      const n = v => Math.round((Number(v) || 0) * 100) / 100;
      const pct = v => (v != null && v !== '') ? `${v}%` : '';
      const agenteDe = r => r.referido_id ? `Referido: ${r.referido_nombre || ''}` : (r.agente_nombre || '');
      const polizaDe = r => (r.poliza_num || '') + (r.es_sobrecomision ? ' (Sobrecom.)' : '');
      const estadoLbl = r => (ESTADO_INFO[r.estado]?.label) || r.estado || '';
      // Columnas por pestaña: [encabezado, valor, esNumérico?]
      const COMUN = [['Póliza', polizaDe], ['Cliente', r => r.cliente || ''], ['Agente', agenteDe]];
      const SPECS = {
        pendientes: [...COMUN,
          ['Tipo', r => r.tipo || ''], ['% Com.', r => pct(r.comision_ramo)],
          ['Prima Cobros', r => n(r.prima), true],
          ['Comisión Total', r => n((Number(r.monto_agente)||0)+(Number(r.monto_ru)||0)), true],
          ['Fecha Pago Cliente', r => fmtFecha(r.fecha_pago_cliente) || ''],
          ['% Ag.', r => pct(r.porcentaje_agente)], ['Monto Agente', r => n(r.monto_agente), true],
          [`% ${abrev}`, r => pct(r.porcentaje_ru)], [`Monto ${abrev}`, r => n(r.monto_ru), true],
          ['Estado', estadoLbl]],
        bolsa: [...COMUN,
          ['Prima Cobros', r => n(r.prima), true],
          ['Comisión Total', r => n((Number(r.monto_agente)||0)+(Number(r.monto_ru)||0)), true],
          ['Fecha Pago Cliente', r => fmtFecha(r.fecha_pago_cliente) || ''],
          ['Monto Agente', r => n(r.monto_agente), true], [`Comisión ${abrev}`, r => n(r.monto_ru), true]],
        aseg: [...COMUN,
          ['Prima', r => n(r.prima), true], [`Monto ${abrev}`, r => n(r.monto_ru), true],
          ['Comprobante', r => r.num_comprobante || ''],
          [`Aseg. pagó a ${abrev}`, r => fmtFecha(r.fecha_recibio_comision) || ''], ['Estado', estadoLbl]],
        agentes: [...COMUN,
          ['Prima', r => n(r.prima), true], ['Monto Agente', r => n(r.monto_agente), true],
          ['Fecha Pago Cliente', r => fmtFecha(r.fecha_pago_cliente) || ''],
          ['Agente cobrado', r => (r.pagado_en ? fmtFecha(r.pagado_en) : fmtFecha(r.fecha_liquidacion)) || '']],
        ru_cobrado: [...COMUN,
          ['Prima', r => n(r.prima), true], ['Pago Cliente', r => fmtFecha(r.fecha_pago_cliente) || ''],
          [`Monto ${abrev}`, r => n(r.monto_ru), true], [`${abrev} cobrado`, r => fmtFecha(r.fecha_ru_cobrado) || '']],
      };
      const TAB_LABEL = { pendientes:'Pendientes', aseg:'Cobrado de Aseguradoras', bolsa:'Por Liquidar', agentes:'Pagado a Agentes', ru_cobrado:`${abrev} Cobrado` };
      const cols = SPECS[tab] || COMUN;
      const label = TAB_LABEL[tab] || 'Comisiones';

      const filtros = [];
      if (filterAgente)      filtros.push('Agente: ' + (agentes.find(a=>String(a.id)===String(filterAgente))?.nombre || ''));
      if (filterAseguradora) filtros.push('Aseguradora: ' + (aseguradoras.find(a=>String(a.id)===String(filterAseguradora))?.nombre || ''));
      if (filterCliente)     filtros.push('Cliente filtrado');
      if (filterPoliza)      filtros.push('Póliza filtrada');
      if (fechaDesde || fechaHasta) filtros.push(`Fechas: ${fechaDesde||'…'} a ${fechaHasta||'…'}`);
      if (tab==='pendientes' && filterEstado) filtros.push('Estado: ' + ({pendiente_aseg:'Esp. Aseguradora', pendiente:'Pendiente Agente', anulada:'Anuladas'}[filterEstado] || filterEstado));

      const aoa = [
        [`${M.nombre} — Comisiones · ` + label],
        [filtros.join('   ·   ')],
        [`${sortedRows.length} registro(s)`],
        [],
        cols.map(c => c[0]),
      ];
      const totales = cols.map(() => 0);
      for (const r of sortedRows) {
        aoa.push(cols.map((c, i) => {
          const v = c[1](r);
          if (c[2]) totales[i] += Number(v) || 0;
          return v;
        }));
      }
      aoa.push([]);
      const totalRow = cols.map((c, i) => c[2] ? n(totales[i]) : '');
      totalRow[0] = 'TOTAL';
      aoa.push(totalRow);

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = cols.map((c, i) => ({ wch: i === 1 ? 26 : (i === 0 ? 18 : 14) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, label.slice(0, 28));
      XLSX.writeFile(wb, `Comisiones_${tab}_${new Date().toISOString().slice(0,10)}.xlsx`);
    } catch (e) {
      alert('No se pudo generar el Excel: ' + (e.message || ''));
    }
  };

  // Registrar pago de aseguradora a MÚLTIPLES comisiones a la vez (mismo comprobante)
  const registrarPagoAsegBulk = async (ids, fecha, remision) => {
    for (const id of ids) {
      await updateRow('comisiones', id, {
        estado: 'pendiente',
        fecha_recibio_comision: fecha,
        num_comprobante: remision || null,
      });
    }
    await load();
  };

  const aprobar = async (r) => {
    if (r.estado !== 'pendiente') { alert(`Solo se pueden aprobar comisiones en estado "Pendiente Agente" (aseguradora ya pagó a ${abrev}).`); return; }
    try { await updateRow('comisiones', r.id, { estado:'aprobado', aprobado_en: new Date().toISOString() }); await load(); }
    catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const marcarPagado = async (r) => {
    try { await updateRow('comisiones', r.id, { estado:'pagado', pagado_en: new Date().toISOString() }); await load(); }
    catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const guardarNota = async () => {
    if (!detalle) return;
    setGuardandoNota(true);
    try {
      await updateRow('comisiones', detalle.id, { notas: notaEdit || null });
      setDetalle(d => ({ ...d, notas: notaEdit || null }));
      await load();
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setGuardandoNota(false); }
  };

  // ── Corregir pago de aseguradora ya registrado ───────────────────────────
  // Caso real (2026-09-03): Gynnela registró el pago de Sura con la fecha equivocada
  // y el único camino era anular y volver a registrar. Aquí se corrige SOLO la
  // metadata (fecha en que pagó la aseguradora + comprobante); el monto recibido y
  // el estado no cambian. Master-only: los demás ven el botón y reciben el aviso.
  const abrirCorregirPagoAseg = async (c) => {
    if (!user?.es_master) {
      alert('Solo un usuario master puede corregir un pago de aseguradora ya registrado.\nPide a un master que haga la corrección.');
      return;
    }
    setCorregirForm({ fecha: c.fecha_recibio_comision || '', comprobante: c.num_comprobante || '', nota: '', lote: false });
    setCorregirLote([]);
    setModalCorregirAseg(c);
    // Un pago de la aseguradora suele cubrir muchas pólizas con la MISMA fecha y
    // comprobante. Se ofrecen para corregirlas juntas (solo si hay comprobante:
    // sin él, "misma fecha" no identifica un pago).
    if (c.fecha_recibio_comision && (c.num_comprobante || '').trim()) {
      const { data } = await supabase.from('comisiones')
        .select('id, notas, num_comprobante, fecha_recibio_comision, polizas(numero)')
        .eq('fecha_recibio_comision', c.fecha_recibio_comision)
        .eq('num_comprobante', c.num_comprobante)
        .neq('estado', 'anulada')
        .neq('id', c.id);
      setCorregirLote(data || []);
    }
  };

  const guardarCorreccionAseg = async () => {
    const c = modalCorregirAseg;
    if (!c || corrigiendo) return;
    const fecha = corregirForm.fecha;
    const comp  = corregirForm.comprobante.trim();
    if (!fecha) { alert('Pon la fecha en que la aseguradora pagó.'); return; }
    const cambios = [];
    if (fecha !== (c.fecha_recibio_comision || '')) cambios.push(`fecha ${fmtFecha(c.fecha_recibio_comision) || '—'} → ${fmtFecha(fecha)}`);
    if (comp  !== (c.num_comprobante || ''))       cambios.push(`comprobante ${c.num_comprobante || '—'} → ${comp || '—'}`);
    if (!cambios.length) { alert('No cambiaste nada.'); return; }
    const traza = `✏️ Pago de aseguradora corregido por ${user?.nombre || user?.email || '—'} el ${fmtFecha(new Date().toISOString().slice(0,10))}: ${cambios.join(' · ')}`
      + (corregirForm.nota.trim() ? ` — ${corregirForm.nota.trim()}` : '');
    const filas = [c, ...(corregirForm.lote ? corregirLote : [])];
    setCorrigiendo(true);
    let ok = 0; const errores = [];
    try {
      for (const r of filas) {
        try {
          await updateRow('comisiones', r.id, {
            fecha_recibio_comision: fecha,
            num_comprobante: comp || null,
            notas: (r.notas ? r.notas + '\n' : '') + traza,
          });
          ok++;
        } catch (e) { errores.push(`${r.polizas?.numero || r.id}: ${e.message || e}`); }
      }
      setModalCorregirAseg(null);
      if (detalle?.id === c.id) {
        const notas = (c.notas ? c.notas + '\n' : '') + traza;
        setDetalle(d => ({ ...d, fecha_recibio_comision: fecha, num_comprobante: comp || null, notas }));
        setNotaEdit(notas);
      }
      await load();
      alert(`✅ ${ok} comisión(es) corregidas.` + (errores.length ? `\n\nErrores: ${errores.join('; ')}` : ''));
    } finally { setCorrigiendo(false); }
  };

  // ── Revertir pago de aseguradora registrado por error ────────────────────
  // Caso real (2026-09-03): Gynnela registró como pagadas comisiones que Sura NO
  // pagó; el Copiloto la mandó a "anular" (que es para comisiones que no debían
  // existir) y las escondió. Esto es el DESHACER de "registrar pago de aseguradora":
  // vuelve a 'pendiente_aseg' y limpia fecha/comprobante/monto recibido. Lo puede
  // hacer cualquier usuario con permiso de editar comisiones (no mueve dinero),
  // pero SOLO mientras la comisión no se haya aprobado ni pagado al agente.
  const esReversibleAseg = (c) => !!c && (
    c.estado === 'pendiente' ||
    (c.estado === 'pendiente_aseg' && Number(c.monto_recibido_aseg || 0) > 0)   // pago parcial
  );

  const abrirRevertirPagoAseg = async (c) => {
    if (!esReversibleAseg(c)) {
      alert('Esta comisión ya se aprobó o se pagó al agente: hay dinero comprometido. Deshaz primero esa liquidación.');
      return;
    }
    setRevertirForm({ nota: '', lote: false });
    setRevertirLote([]);
    setModalRevertirAseg(c);
    // Mismo pago = misma fecha + comprobante (solo si hay comprobante). Se ofrecen
    // únicamente las que también se pueden revertir (aún sin aprobar).
    if (c.fecha_recibio_comision && (c.num_comprobante || '').trim()) {
      const { data } = await supabase.from('comisiones')
        .select('id, notas, estado, num_comprobante, fecha_recibio_comision, monto_recibido_aseg, polizas(numero)')
        .eq('fecha_recibio_comision', c.fecha_recibio_comision)
        .eq('num_comprobante', c.num_comprobante)
        .eq('estado', 'pendiente')
        .neq('id', c.id);
      setRevertirLote(data || []);
    }
  };

  const confirmarRevertirPagoAseg = async () => {
    const c = modalRevertirAseg;
    if (!c || revirtiendoAseg) return;
    const quien = user?.nombre || user?.email || '—';
    const hoy   = fmtFecha(new Date().toISOString().slice(0,10));
    const trazaDe = (r) => `↩️ Pago de aseguradora revertido por ${quien} el ${hoy}`
      + ` (tenía: fecha ${fmtFecha(r.fecha_recibio_comision) || '—'} · comprobante ${r.num_comprobante || '—'} · recibido ${fmt$(r.monto_recibido_aseg || 0)})`
      + (revertirForm.nota.trim() ? ` — ${revertirForm.nota.trim()}` : '');
    const filas = [c, ...(revertirForm.lote ? revertirLote : [])];
    setRevirtiendoAseg(true);
    let ok = 0; const errores = [];
    try {
      for (const r of filas) {
        try {
          await updateRow('comisiones', r.id, {
            estado: 'pendiente_aseg',
            monto_recibido_aseg: 0,
            fecha_recibio_comision: null,
            num_comprobante: null,
            notas: (r.notas ? r.notas + '\n' : '') + trazaDe(r),
          });
          ok++;
        } catch (e) { errores.push(`${r.polizas?.numero || r.id}: ${e.message || e}`); }
      }
      setModalRevertirAseg(null);
      setDetalle(null);
      await load();
      alert(`✅ ${ok} comisión(es) devueltas a "Esp. Aseguradora".` + (errores.length ? `\n\nErrores: ${errores.join('; ')}` : ''));
    } finally { setRevirtiendoAseg(false); }
  };

  const openDetalle = async (r) => {
    setDetalle(r);
    setNotaEdit(r.notas || '');
    setLoadingDetalle(true);
    setDetalleData(null);
    try {
      // Todos los pagos de esa póliza
      const { data: pagos } = await supabase.from('pagos')
        .select('*')
        .eq('poliza_id', r.poliza_id)
        .order('cuota_num', { ascending: true });

      // Todas las comisiones históricas de esa póliza (incluye la fecha en que el
      // cliente pagó el cobro/cuota que generó cada comisión — clave para detectar duplicados)
      const { data: histCom } = await supabase.from('comisiones')
        .select('*, agentes(nombre), cobros_recibidos(fecha), pagos(fecha_pago)')
        .eq('poliza_id', r.poliza_id)
        .order('creado_en', { ascending: false });
      const histComEnriched = (histCom || []).map(c => ({
        ...c,
        _fecha_pago_cliente: c.cobros_recibidos?.fecha || c.pagos?.fecha_pago || c.fecha_recibio_comision || null,
      }));

      // Info completa de la póliza
      const { data: polizaFull } = await supabase.from('polizas')
        .select('*, aseguradoras(nombre), ramos(nombre), subramos(nombre)')
        .eq('id', r.poliza_id)
        .single();

      setDetalleData({ pagos: pagos || [], histCom: histComEnriched, poliza: polizaFull });
    } catch (e) { alert('Error al cargar detalle: ' + e.message); }
    finally { setLoadingDetalle(false); }
  };

  // ── Anular una comisión creada por error (duplicada o error de BD) ──
  // Soft-delete: estado='anulada' + motivo/quién/cuándo. Deja de contar como ingreso
  // y se excluye de todos los totales/pestañas, pero queda auditable y reversible.
  const abrirAnular = (c) => {
    setModalAnular(c);
    setAnularTipo('duplicada');
    setAnularDupId('');
    setAnularTexto('');
  };

  const confirmarAnular = async () => {
    if (anulando || !modalAnular) return;
    if (anularTipo === 'duplicada' && !anularDupId) { alert('Selecciona la comisión original (de la que ésta es duplicada).'); return; }
    if (anularTipo === 'error' && !anularTexto.trim()) { alert('Escribe el motivo de la anulación.'); return; }
    setAnulando(true);
    try {
      let motivo;
      if (anularTipo === 'duplicada') {
        // Merge inteligente: si la duplicada está MÁS avanzada que la original que se queda,
        // copiar su gestión a la original ANTES de anularla (para no perder el trabajo hecho).
        const original = (detalleData?.histCom || []).find(c => String(c.id) === String(anularDupId));
        if (original) {
          const rankA = ESTADO_RANK[modalAnular.estado] ?? -1;
          const rankB = ESTADO_RANK[original.estado] ?? -1;
          if (rankA > rankB) {
            await updateRow('comisiones', original.id, {
              estado:                 modalAnular.estado,
              fecha_recibio_comision: original.fecha_recibio_comision || modalAnular.fecha_recibio_comision || null,
              aprobado_en:            original.aprobado_en            || modalAnular.aprobado_en            || null,
              pagado_en:              original.pagado_en              || modalAnular.pagado_en              || null,
              fecha_liquidacion:      original.fecha_liquidacion      || modalAnular.fecha_liquidacion      || null,
              num_comprobante:        original.num_comprobante        || modalAnular.num_comprobante        || null,
              ru_liquidado_en:        original.ru_liquidado_en        || modalAnular.ru_liquidado_en        || null,
            });
          }
        }
        motivo = `Duplicada de comisión #${anularDupId}` + (anularTexto.trim() ? ` — ${anularTexto.trim()}` : '');
      } else {
        motivo = `Error de BD — ${anularTexto.trim()}`;
      }
      await updateRow('comisiones', modalAnular.id, {
        estado:           'anulada',
        estado_previo:    modalAnular.estado,
        motivo_anulacion: motivo,
        anulado_en:       new Date().toISOString(),
        anulado_por:      user?.nombre || user?.email || 'Sistema',
        duplicada_de_id:  anularTipo === 'duplicada' ? parseInt(anularDupId) : null,
      });
      setModalAnular(null); setDetalle(null);
      await load();
    } catch (e) {
      alert('Error al anular la comisión: ' + (e.message || e));
    } finally { setAnulando(false); }
  };

  const revertirAnulacion = async (c) => {
    if (!window.confirm('¿Revertir la anulación y devolver la comisión a su estado anterior?')) return;
    try {
      await updateRow('comisiones', c.id, {
        estado: c.estado_previo || 'pendiente_aseg',
        motivo_anulacion: null, anulado_en: null, anulado_por: null, duplicada_de_id: null, estado_previo: null,
      });
      setDetalle(null);
      await load();
    } catch (e) { alert('Error al revertir: ' + (e.message || e)); }
  };

  const recalcularComisiones = async () => {
    if (!window.confirm('¿Recalcular comisiones con porcentaje 0% usando el % actual de cada agente en la póliza?')) return;
    setRecalculando(true);
    try {
      // Traer comisiones con agente pero porcentaje_agente = 0 o null
      const { data: comCero } = await supabase.from('comisiones')
        .select('id, poliza_id, agente_id, prima, monto_ru, porcentaje_agente')
        .or('porcentaje_agente.is.null,porcentaje_agente.eq.0')
        .not('agente_id', 'is', null);

      // Traer todos los poliza_agentes relevantes
      const polizaIds = [...new Set((comCero || []).map(c => c.poliza_id))];
      const agenteIds = [...new Set((comCero || []).map(c => c.agente_id).filter(Boolean))];
      let paMap = {};
      if (polizaIds.length > 0) {
        const { data: pas } = await supabase.from('poliza_agentes')
          .select('poliza_id, agente_id, porcentaje')
          .in('poliza_id', polizaIds);
        for (const pa of (pas || [])) {
          paMap[`${pa.poliza_id}_${pa.agente_id}`] = parseFloat(pa.porcentaje) || 0;
        }
      }

      // Tasas por defecto del agente como fallback
      let agenteRates = {};
      if (agenteIds.length > 0) {
        const { data: ags } = await supabase.from('agentes')
          .select('id, comision_nueva_agente, comision_renovacion_agente')
          .in('id', agenteIds);
        for (const a of (ags || [])) agenteRates[a.id] = a;
      }

      // Traer comision_ramo y tipo de cada póliza
      let polizaMap2 = {};
      if (polizaIds.length > 0) {
        const { data: pols } = await supabase.from('polizas').select('id, comision_ramo, tipo').in('id', polizaIds);
        for (const p of (pols || [])) polizaMap2[p.id] = p;
      }

      let corregidas = 0;
      for (const c of (comCero || [])) {
        // Buscar % del agente: primero poliza_agentes, luego tasa default del agente según tipo
        let pctAg = paMap[`${c.poliza_id}_${c.agente_id}`] || 0;
        if (pctAg === 0 && c.agente_id && agenteRates[c.agente_id]) {
          const ag = agenteRates[c.agente_id];
          const esRenov = (c.tipo || polizaMap2[c.poliza_id]?.tipo) === 'renovacion';
          pctAg = parseFloat(esRenov ? ag.comision_renovacion_agente : ag.comision_nueva_agente) || 0;
        }
        if (pctAg === 0) continue;
        const comisionRU = (parseFloat(c.prima) || 0) * (parseFloat(polizaMap2[c.poliza_id]?.comision_ramo) || 0) / 100;
        const montoAg = Math.round(comisionRU * pctAg / 100 * 100) / 100;
        const pctRU   = Math.max(0, 100 - pctAg);
        const montoRU = Math.round(comisionRU * pctRU / 100 * 100) / 100;
        await updateRow('comisiones', c.id, {
          porcentaje_agente: pctAg, monto_agente: montoAg,
          porcentaje_ru: pctRU, monto_ru: montoRU,
        });
        corregidas++;
      }
      alert(`✅ ${corregidas} comisión(es) recalculada(s).`);
      await load();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setRecalculando(false); }
  };

  const totalAgentePorPoliza = detalleData?.histCom
    ?.filter(c => c.agente_id === detalle?.agente_id && c.estado === 'pagado')
    ?.reduce((s, c) => s + Number(c.monto_agente || 0), 0) || 0;

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortedRows = useMemo(() => {
    let base = rows;
    // Filtro por rango de fechas (sobre la fecha que muestra cada pestaña)
    if (fechaDesde || fechaHasta) {
      const fechaRef = (r) => tab === 'agentes' ? r.fecha_cobro_agente
                            : tab === 'aseg'    ? r.fecha_recibio_comision
                            : tab === 'ru_cobrado' ? r.fecha_ru_cobrado
                            : r.fecha_pago_cliente;
      base = rows.filter(r => {
        const f = fechaRef(r);
        if (!f) return false;
        const fd = String(f).slice(0, 10);
        if (fechaDesde && fd < fechaDesde) return false;
        if (fechaHasta && fd > fechaHasta) return false;
        return true;
      });
    }
    if (!sortCol) return base;
    return [...base].sort((a, b) => {
      let va = a[sortCol] ?? '';
      let vb = b[sortCol] ?? '';
      const numA = Number(va), numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB) && va !== '' && vb !== '') {
        return sortDir === 'asc' ? numA - numB : numB - numA;
      }
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb), 'es')
        : String(vb).localeCompare(String(va), 'es');
    });
  }, [rows, sortCol, sortDir, fechaDesde, fechaHasta, tab]);

  const Th = ({ col, children, style }) => {
    const active = sortCol === col;
    return (
      <th onClick={() => toggleSort(col)}
        style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap',
          color: active ? 'var(--sky-light)' : undefined, ...style }}>
        {children} <span style={{ fontSize:10, opacity: active ? 1 : 0.4 }}>{active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
      </th>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Comisiones</div>
          <div className="page-subtitle">{sortedRows.length} registros{filterAgente ? ` · ${agentes.find(a=>String(a.id)===String(filterAgente))?.nombre || ''}` : ''}{(fechaDesde || fechaHasta) ? ' · filtrado por fecha' : ''}</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {canEdit('comisiones') && tab === 'pendientes' && !esAgenteRestringido && (
            <button className="btn btn-primary" onClick={abrirPagoAsegMasivo}
              title="Marca varias comisiones como recibidas de la aseguradora; puedes editar el monto recibido por póliza">
              <Building size={15}/> Registrar pago de aseguradora
            </button>
          )}
          {canEdit('comisiones') && (
            <button className="btn btn-secondary" onClick={() => setModalEstadoCuenta(true)}
              title="Sube el estado de cuenta de comisiones de una aseguradora para conciliar y aplicar pagos">
              <FileText size={15}/> Comisiones Aseguradora
            </button>
          )}
        </div>
      </div>


      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'1px solid rgba(var(--silver-rgb),0.2)', paddingBottom:0 }}>
        {[
          // "Pendientes" (esperando aseguradora) es gestión de la operación: oculto a agentes.
          ...(esAgenteRestringido ? [] : [{ key:'pendientes', label:'⏳ Pendientes' }]),
          { key:'aseg',       label:'🏦 Cobrado de Aseguradoras' },
          { key:'bolsa',      label:'💼 Por Liquidar' },
          { key:'agentes',    label:'✅ Pagado a Agentes' },
          // "RU Cobrado" es interno de RU: no se muestra a agentes restringidos
          ...(esAgenteRestringido ? [] : [{ key:'ru_cobrado', label:`🏢 ${abrev} Cobrado` }]),
        ].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setFilterPoliza(''); setSortCol(''); setSortDir('desc'); }}
            style={{ padding:'8px 18px', background:'none', border:'none', borderBottom: tab===t.key ? '2px solid var(--sky)' : '2px solid transparent',
              color: tab===t.key ? 'var(--sky)' : 'var(--silver)', fontWeight: tab===t.key ? 700 : 400,
              cursor:'pointer', fontSize:13, marginBottom:-1, transition:'all 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Stat cards. Primera tarjeta = TOTAL de comisiones que se espera de las
          aseguradoras (porción agentes + porción de la casa); luego el desglose. */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:16, marginBottom:24 }}>
        {!esAgenteRestringido && (
        <div className="stat-card" style={{ borderColor:'rgba(var(--sky-rgb),0.4)' }}>
          <DollarSign size={40} className="stat-icon" style={{ color:'var(--sky)' }} />
          <div className="stat-value" style={{ color:'var(--sky)' }}>{fmt$(totales.esp_total ?? (totales.esp_aseg + totales.ru_esp_aseg))}</div>
          <div className="stat-label">Comisiones esperadas (total)</div>
        </div>
        )}
        {!esAgenteRestringido && (
        <div className="stat-card">
          <Building size={40} className="stat-icon" style={{ color:'var(--sky-light)' }} />
          <div className="stat-value" style={{ color:'var(--sky-light)' }}>{fmt$(totales.ru_esp_aseg)}</div>
          <div className="stat-label">{abrev}: esperando aseguradora</div>
        </div>
        )}
        {!esAgenteRestringido && (
        <div className="stat-card">
          <Building size={40} className="stat-icon" style={{ color:'var(--text-purple)' }} />
          <div className="stat-value" style={{ color:'var(--text-purple)' }}>{fmt$(totales.esp_aseg)}</div>
          <div className="stat-label">Agentes: esperando aseguradora</div>
        </div>
        )}
        <div className="stat-card">
          <DollarSign size={40} className="stat-icon" style={{ color:'var(--text-warning)' }} />
          <div className="stat-value" style={{ color:'var(--text-warning)' }}>{fmt$(totales.pendiente_agente)}</div>
          <div className="stat-label">Por liquidar a Agentes</div>
        </div>
        {!esAgenteRestringido && (
        <div className="stat-card">
          <DollarSign size={40} className="stat-icon" style={{ color:'var(--text-success)' }} />
          <div className="stat-value" style={{ color:'var(--text-success)' }}>{fmt$(totales.ru_total)}</div>
          <div className="stat-label">Comisión {abrev} (listo para liquidar)</div>
        </div>
        )}
      </div>

      {/* Toolbar con filtros */}
      <div className="toolbar">
        <PolizaSelect value={filterPoliza} onChange={setFilterPoliza} width={220} />
        <ClienteSelect value={filterCliente} onChange={setFilterCliente} placeholder="Filtrar por cliente..." width={220} />
        {!esAgenteRestringido && (
        <select className="form-select" style={{ width:180 }} value={filterAgente} onChange={e=>setFilterAgente(e.target.value)}>
          <option value="">Todos los agentes</option>
          {agentes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        )}
        <select className="form-select" style={{ width:180 }} value={filterAseguradora} onChange={e=>setFilterAseguradora(e.target.value)}>
          <option value="">Todas las aseguradoras</option>
          {aseguradoras.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}
          title="Filtra por la fecha que muestra la columna de fecha de esta pestaña">
          <input type="date" className="form-input" style={{ width:150 }} value={fechaDesde}
            onChange={e=>setFechaDesde(e.target.value)} title="Desde"/>
          <span style={{ color:'var(--silver)', fontSize:12 }}>a</span>
          <input type="date" className="form-input" style={{ width:150 }} value={fechaHasta}
            onChange={e=>setFechaHasta(e.target.value)} title="Hasta"/>
          {(fechaDesde || fechaHasta) && (
            <button className="btn btn-secondary btn-sm" title="Limpiar fechas"
              onClick={() => { setFechaDesde(''); setFechaHasta(''); }}>✕</button>
          )}
        </div>
        {tab === 'pendientes' && (<>
          <select className="form-select" style={{ width:190 }} value={filterEstado} onChange={e=>setFilterEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="pendiente_aseg">Esp. Aseguradora</option>
            <option value="pendiente">Pendiente Agente</option>
            <option value="anulada">Anuladas</option>
          </select>
        </>)}
        <button className="btn btn-secondary" style={{ marginLeft:'auto' }} onClick={exportarComisionesExcel} disabled={sortedRows.length === 0}
          title="Descarga a Excel la lista de esta pestaña, tal como se ve, con los filtros aplicados">
          <FileText size={15}/> Descargar Excel
        </button>
      </div>

      {/* Resumen por agente + bolsa RU — solo tab bolsa */}
      {tab === 'bolsa' && (rows.length > 0 || bolsaRU.total > 0) && (() => {
        // Agrupar por DESTINATARIO: agente o referido (mismo flujo de liquidación).
        const porPayee = {};
        rows.forEach(r => {
          const esRef = !!r.referido_id;
          // Excluir filas de RU puras (sin agente y sin referido): van por la bolsa RU.
          if (!esRef && r.agente_id == null) return;
          const key = esRef ? `ref:${r.referido_id}` : `ag:${r.agente_id}`;
          if (!porPayee[key]) porPayee[key] = {
            key, esRef, id: esRef ? r.referido_id : r.agente_id,
            nombre: esRef ? (r.referido_nombre || `Referido #${r.referido_id}`) : (r.agente_nombre || 'Sin agente'),
            total: 0, count: 0,
          };
          porPayee[key].total += Number(r.monto_agente || 0);
          porPayee[key].count++;
        });
        return (
          <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:20 }}>
            {Object.values(porPayee).map(p => (
              <div key={p.key} style={{ padding:'14px 20px', background: p.esRef ? 'rgba(124, 58, 237, 0.12)' : 'rgba(var(--steel-rgb),0.5)', border: p.esRef ? '1px solid rgba(167, 139, 250, 0.4)' : '1px solid rgba(var(--sky-rgb),0.3)', borderRadius:12, minWidth:220, display:'flex', flexDirection:'column', gap:6 }}>
                <div style={{ fontSize:12, color: p.esRef ? '#a78bfa' : 'var(--silver)', fontWeight:700 }}>{p.esRef ? '👤 Referido · ' : ''}{p.nombre}</div>
                <div style={{ fontSize:22, fontWeight:800, color: p.esRef ? '#a78bfa' : 'var(--sky)' }}>{fmt$(p.total)}</div>
                <div style={{ fontSize:11, color:'var(--silver)' }}>{p.count} comisión(es) {p.esRef ? 'de referido' : 'aprobadas'}</div>
                {canEdit('comisiones') && (
                  <button className="btn btn-success" style={{ marginTop:4 }} onClick={() => abrirLiquidarPayee(p)}>
                    <Send size={13}/> Liquidar a {(p.nombre||'').split(' ')[0]}
                  </button>
                )}
              </div>
            ))}

            {/* Bolsa de RU — interna de RU; no se muestra a agentes restringidos */}
            {!esAgenteRestringido && (
            <div style={{ padding:'14px 20px', background:'rgba(46, 196, 125, 0.08)', border:'1px solid rgba(46, 196, 125, 0.35)', borderRadius:12, minWidth:220, display:'flex', flexDirection:'column', gap:6 }}>
              <div style={{ fontSize:12, color:'var(--text-success)', fontWeight:700 }}>🏢 {getMarca().nombre}</div>
              <div style={{ fontSize:22, fontWeight:800, color:'var(--text-success)' }}>{fmt$(bolsaRU.total)}</div>
              <div style={{ fontSize:11, color:'var(--silver)' }}>{bolsaRU.count} comisión(es) en bolsa {abrev}</div>
              {canEdit('comisiones') && bolsaRU.total > 0 && (
                <button className="btn btn-success" style={{ marginTop:4 }} onClick={() => { setFechaCierreRU(new Date().toISOString().slice(0,10)); setModalCierreRU(true); }}>
                  <Send size={13}/> Cerrar bolsa {abrev}
                </button>
              )}
            </div>
            )}
          </div>
        );
      })()}

      {/* Total pagado a agentes en el periodo — solo tab agentes */}
      {tab === 'agentes' && (() => {
        const filas = sortedRows.filter(r => !r.referido_id);
        const totalAgentes = filas.reduce((s, r) => s + Number(r.monto_agente || 0), 0);
        const agenteSel = filterAgente ? (agentes.find(a => String(a.id) === String(filterAgente))?.nombre || '') : '';
        return (
          <div style={{ marginBottom:20 }}>
            <div style={{ padding:'16px 24px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.35)', borderRadius:12, maxWidth:540 }}>
              <div style={{ fontSize:12, color:'var(--sky-light)', fontWeight:700 }}>
                ✅ TOTAL PAGADO {agenteSel ? `A ${agenteSel.toUpperCase()}` : 'A AGENTES'}{(fechaDesde||fechaHasta) ? ' (periodo filtrado)' : ''}
              </div>
              <div style={{ fontSize:28, fontWeight:800, color:'var(--sky)' }}>{fmt$(totalAgentes)}</div>
              <div style={{ fontSize:11, color:'var(--silver)' }}>
                {filas.length} comisión(es){(fechaDesde||fechaHasta) ? ` · ${fmtFecha(fechaDesde)||'…'} a ${fmtFecha(fechaHasta)||'…'}` : ''}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Total + cierres RU — solo tab ru_cobrado */}
      {tab === 'ru_cobrado' && (() => {
        // Agrupar por cierre (ru_liquidado_en exacto: todas las del mismo cierre comparten timestamp)
        const porCierre = {};
        sortedRows.forEach(r => {
          const ts = r.ru_liquidado_en;
          if (!ts) return;
          if (!porCierre[ts]) porCierre[ts] = { ts, total: 0, count: 0 };
          porCierre[ts].total += Number(r.monto_ru || 0);
          porCierre[ts].count++;
        });
        const cierres = Object.values(porCierre).sort((a, b) => String(b.ts).localeCompare(String(a.ts)));
        const totalGeneral = sortedRows.reduce((s, r) => s + Number(r.monto_ru || 0), 0);
        return (
          <div style={{ marginBottom:20 }}>
            <div style={{ padding:'16px 24px', background:'rgba(46, 196, 125, 0.08)', border:'1px solid rgba(46, 196, 125, 0.35)', borderRadius:12, maxWidth:540 }}>
              <div style={{ fontSize:12, color:'var(--text-success)', fontWeight:700 }}>🏢 {abrev.toUpperCase()} COBRADO{(fechaDesde||fechaHasta) ? ' (periodo filtrado)' : ' (total)'}</div>
              <div style={{ fontSize:28, fontWeight:800, color:'var(--text-success)' }}>{fmt$(totalGeneral)}</div>
              <div style={{ fontSize:11, color:'var(--silver)', marginBottom: cierres.length ? 8 : 0 }}>{sortedRows.length} comisión(es) cerradas · {cierres.length} cierre(s)</div>
              {/* Cierres en filas compactas dentro de la misma tarjeta */}
              {cierres.map(c => (
                <div key={c.ts} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'8px 0', borderTop:'1px solid rgba(46, 196, 125, 0.18)' }}>
                  <div style={{ fontSize:12, color:'var(--silver)' }}>
                    <span style={{ color:'var(--silver)' }}>Cierre {fmtFechaHora(c.ts)}</span>
                    {' · '}<b style={{ color:'var(--text-success)' }}>{fmt$(c.total)}</b>
                    {' · '}{c.count} com.
                  </div>
                  {user?.es_master && (
                    <button className="btn btn-sm btn-secondary" style={{ color:'var(--text-warning)', borderColor:'rgba(245, 158, 11, 0.4)', whiteSpace:'nowrap' }}
                      onClick={() => setModalReverso({ cierre_ts: c.ts, total: c.total, count: c.count })}
                      title="Revertir este cierre — devuelve las comisiones a la bolsa (solo superusuario)">
                      ↩️ Revertir
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Tabla */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <Th col="poliza_num">Póliza</Th><Th col="cliente">Cliente</Th><Th col="agente_nombre">Agente</Th>
              {tab === 'pendientes' && <><Th col="tipo">Tipo</Th><Th col="comision_ramo">% Com.</Th><Th col="prima">Prima Cobros</Th><th title={`Comisión total que paga la aseguradora por este cobro, antes de dividir agente/${abrev}`}>Comisión Total</th><Th col="fecha_pago_cliente">Fecha Pago Cliente</Th><Th col="porcentaje_agente">% Ag.</Th><Th col="monto_agente">Monto Agente</Th><Th col="porcentaje_ru">% {abrev}</Th><Th col="monto_ru">Monto {abrev}</Th><Th col="estado">Estado</Th></>}
              {tab === 'bolsa'     && <><Th col="prima">Prima Cobros</Th><th title={`Comisión total que paga la aseguradora por este cobro, antes de dividir agente/${abrev}`}>Comisión Total</th><Th col="fecha_pago_cliente">Fecha Pago Cliente</Th><Th col="monto_agente">Monto Agente</Th><Th col="monto_ru">Comisión {abrev}</Th></>}
              {tab === 'aseg'      && <><Th col="prima">Prima</Th><Th col="monto_ru">Monto {abrev}</Th><Th col="num_comprobante">Comprobante</Th><Th col="fecha_recibio_comision">Aseg. pagó a {abrev}</Th><Th col="estado">Estado</Th></>}
              {tab === 'agentes'   && <><Th col="prima">Prima</Th><Th col="monto_agente">Monto Agente</Th><Th col="fecha_pago_cliente">Fecha Pago Cliente</Th><Th col="fecha_cobro_agente">Agente cobrado</Th></>}
              {tab === 'ru_cobrado' && <><Th col="prima">Prima</Th><Th col="fecha_pago_cliente">Pago Cliente</Th><Th col="monto_ru">Monto {abrev}</Th><Th col="fecha_ru_cobrado">{abrev} cobrado</Th></>}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr><td colSpan={10}>
                <div className="empty-state">
                  <div className="empty-state-icon"><DollarSign size={48} /></div>
                  <div className="empty-state-title">Sin registros</div>
                  <div>Se generan al aplicar cobros o al importar liquidaciones</div>
                </div>
              </td></tr>
            ) : sortedRows.map(r => (
              <tr key={r.id} style={r.estado === 'anulada' ? { opacity:0.55, textDecoration:'line-through', background:'rgba(239, 68, 68, 0.04)' } : {}}>
                <td>
                  <button onClick={() => verPolizaCompleta(r.poliza_id)} style={{ background:'none', border:'none', cursor:'pointer', fontWeight:700, color:'var(--sky)', padding:0, fontFamily:'inherit', fontSize:'inherit' }}>
                    {r.poliza_num}
                  </button>
                  {r.es_sobrecomision && (
                    <span title="Sobrecomisión — la aseguradora la paga aparte de la comisión normal"
                      style={{ display:'inline-block', marginLeft:6, padding:'1px 6px', borderRadius:5, fontSize:10, fontWeight:700, color:'var(--text-warning)', background:'rgba(245, 158, 11, 0.12)', border:'1px solid rgba(245, 158, 11, 0.35)', verticalAlign:'middle' }}>
                      SOBRECOM.
                    </span>
                  )}
                  {/* Un ajuste dice a la vista a qué se refiere: el agente no tiene que adivinar un negativo. */}
                  {conceptoDeAjuste(r.notas) && (
                    <div style={{ marginTop:3, maxWidth:260, fontSize:10, lineHeight:1.35, color:'var(--text-warning)', whiteSpace:'normal' }}>
                      <strong>AJUSTE:</strong> {conceptoDeAjuste(r.notas)}
                    </div>
                  )}
                </td>
                <td style={{ fontSize:12 }}>{r.cliente}</td>
                <td style={{ fontSize:12 }}>
                  {r.referido_id
                    ? <span style={{ color:'#a78bfa', fontWeight:600 }}>Referido: {r.referido_nombre}</span>
                    : (r.agente_nombre || '—')}
                </td>

                {/* Columnas tab Pendientes */}
                {tab === 'pendientes' && <>
                  <td><span className={`badge ${r.tipo === 'renovacion' ? 'badge-info' : 'badge-success'}`}>{r.tipo}</span></td>
                  <td style={{ fontSize:12, color:'var(--sky-light)', fontWeight:600 }}>{r.comision_ramo != null ? `${r.comision_ramo}%` : '—'}</td>
                  <td style={{ fontSize:13 }}>{fmt$(r.prima)}</td>
                  <td className="font-bold" style={{ fontSize:13, color:'var(--sky-light)' }}>{fmt$((Number(r.monto_agente)||0)+(Number(r.monto_ru)||0))}</td>
                  <td style={{ fontSize:11, color: r.fecha_pago_cliente ? 'var(--sky-light)' : 'var(--silver)' }}>{fmtFecha(r.fecha_pago_cliente) || '—'}</td>
                  <td className="text-silver" style={{ fontSize:12 }}>{fmtPct(r.porcentaje_agente)}</td>
                  <td className="font-bold" style={{ color:'var(--sky)' }}>{fmt$(r.monto_agente)}</td>
                  <td className="text-silver" style={{ fontSize:12 }}>{fmtPct(r.porcentaje_ru)}</td>
                  <td className="font-bold" style={{ color:'var(--text-success)' }}>{fmt$(r.monto_ru)}</td>
                  <td>{(() => {
                    const ei = ESTADO_INFO[r.estado] || { label:r.estado, color:'var(--silver)', bg:'transparent', border:'rgba(var(--silver-rgb),0.2)' };
                    // Pago PARCIAL de la aseguradora: sigue esperando, pero ya entró algo.
                    const rec = Number(r.monto_recibido_aseg || 0);
                    const esp = (Number(r.monto_agente)||0) + (Number(r.monto_ru)||0);
                    const parcial = r.estado === 'pendiente_aseg' && rec > 0.005;
                    return (
                      <>
                        <span style={{ padding:'3px 8px', borderRadius:6, fontSize:11, fontWeight:700, color:ei.color, background:ei.bg, border:`1px solid ${ei.border}` }}>{ei.label}</span>
                        {parcial && (
                          <span title={`La aseguradora pagó ${fmt$(rec)} de ${fmt$(esp)}. Falta ${fmt$(esp - rec)}.`}
                            style={{ display:'block', marginTop:3, fontSize:10, fontWeight:700, color:'var(--text-warning)' }}>
                            parcial · falta {fmt$(esp - rec)}
                          </span>
                        )}
                      </>
                    );
                  })()}</td>
                </>}

                {/* Columnas tab Bolsa / Por Liquidar */}
                {tab === 'bolsa' && <>
                  <td style={{ fontSize:13 }}>{fmt$(r.prima)}</td>
                  <td className="font-bold" style={{ fontSize:13, color:'var(--sky-light)' }}>{fmt$((Number(r.monto_agente)||0)+(Number(r.monto_ru)||0))}</td>
                  <td style={{ fontSize:11, color: r.fecha_pago_cliente ? 'var(--sky-light)' : 'var(--silver)' }}>{fmtFecha(r.fecha_pago_cliente) || '—'}</td>
                  <td className="font-bold" style={{ color:'var(--sky)' }}>{fmt$(r.monto_agente)}</td>
                  <td className="font-bold" style={{ color:'var(--text-success)' }}>{fmt$(r.monto_ru)}</td>
                </>}

                {/* Columnas tab Aseg → RU */}
                {tab === 'aseg' && <>
                  <td style={{ fontSize:13 }}>{fmt$(r.prima)}</td>
                  <td className="font-bold" style={{ color:'var(--text-success)' }}>{fmt$(r.monto_ru)}</td>
                  <td style={{ fontSize:11, color:'var(--silver)' }}>{r.num_comprobante || '—'}</td>
                  <td style={{ fontSize:11, color:'var(--sky-light)', fontWeight:600 }}>{fmtFecha(r.fecha_recibio_comision) || '—'}</td>
                  <td>{(() => { const ei = ESTADO_INFO[r.estado] || { label:r.estado, color:'var(--silver)', bg:'transparent', border:'rgba(var(--silver-rgb),0.2)' }; return <span style={{ padding:'3px 8px', borderRadius:6, fontSize:11, fontWeight:700, color:ei.color, background:ei.bg, border:`1px solid ${ei.border}` }}>{ei.label}</span>; })()}</td>
                </>}

                {/* Columnas tab Pagado a Agentes */}
                {tab === 'agentes' && <>
                  <td style={{ fontSize:13 }}>{fmt$(r.prima)}</td>
                  <td className="font-bold" style={{ color:'var(--sky)' }}>{fmt$(r.monto_agente)}</td>
                  <td style={{ fontSize:11, color: r.fecha_pago_cliente ? 'var(--sky-light)' : 'var(--silver)' }}>{fmtFecha(r.fecha_pago_cliente) || '—'}</td>
                  <td style={{ fontSize:11, color:'var(--text-success)', fontWeight:600 }}>
                    {r.pagado_en ? fmtFecha(r.pagado_en) : (fmtFecha(r.fecha_liquidacion) || '—')}
                  </td>
                </>}

                {/* Columnas tab RU Cobrado */}
                {tab === 'ru_cobrado' && <>
                  <td style={{ fontSize:13 }}>{fmt$(r.prima)}</td>
                  <td style={{ fontSize:11, color: r.fecha_pago_cliente ? 'var(--sky-light)' : 'var(--silver)' }}>{fmtFecha(r.fecha_pago_cliente) || '—'}</td>
                  <td className="font-bold" style={{ color:'var(--text-success)' }}>{fmt$(r.monto_ru)}</td>
                  <td style={{ fontSize:11, color:'var(--text-success)', fontWeight:600 }}>{fmtFecha(r.fecha_ru_cobrado) || '—'}</td>
                </>}

                <td>
                  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                    {r.notas && <span title={r.notas} style={{ fontSize:14, cursor:'default' }}>💬</span>}
                    <button className="btn btn-sm btn-secondary" onClick={() => openDetalle(r)} title="Ver detalle">
                      <Eye size={12}/> Ver
                    </button>
                    {r.estado === 'pendiente_aseg' && canEdit('comisiones') && (
                      <button className="btn btn-sm btn-secondary" style={{ color:'var(--text-purple)', borderColor:'rgba(139, 92, 246, 0.4)' }}
                        onClick={() => { setModalAseg(r); setFechaAseg(new Date().toISOString().slice(0,10)); setNumRemision(''); }}
                        title="Registrar que la aseguradora nos pagó esta comisión">
                        <Building size={12}/> Aseg. pagó
                      </button>
                    )}
                    {r.estado === 'pendiente' && canEdit('comisiones') && (
                      <button className="btn btn-sm btn-secondary" onClick={() => aprobar(r)}>
                        <CheckCircle size={12}/> Aprobar
                      </button>
                    )}
                    {r.estado === 'aprobado' && canEdit('comisiones') && (
                      <button className="btn btn-sm btn-success" onClick={() => r.referido_id
                          ? abrirLiquidarPayee({ esRef:true, id:r.referido_id, nombre: r.referido_nombre || ('Referido #'+r.referido_id) })
                          : abrirLiquidarAgente(r.agente_id)}
                        title={r.referido_id ? "Liquidar las comisiones aprobadas de este referido" : "Liquidar todas las comisiones aprobadas de este agente"}>
                        <Send size={12}/> Liquidar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal: Registrar pago de aseguradora a RU */}
      {/* Pago recibido de aseguradora — varias comisiones, con monto real por póliza */}
      {modalPagoAsegMasivo && (() => {
        const items = sortedRows.filter(r => r.estado === 'pendiente_aseg');
        const marcados = items.filter(r => pagoAsegSel[r.id]?.sel);
        const totalEsperado = marcados.reduce((s, r) => s + saldoAsegDe(r), 0);
        const totalRecibido = marcados.reduce((s, r) => s + (Number(pagoAsegSel[r.id]?.recibido) || 0), 0);
        const dif = r2(totalEsperado - totalRecibido);
        const setSel = (id, v) => setPagoAsegSel(p => ({ ...p, [id]: { ...(p[id] || {}), sel: v } }));
        const setMonto = (id, v) => setPagoAsegSel(p => ({ ...p, [id]: { ...(p[id] || {}), recibido: v } }));
        const todos = marcados.length === items.length;
        return (
          <div className="modal-overlay" onClick={() => !guardandoPagoAseg && setModalPagoAsegMasivo(false)}>
            <div className="modal modal-lg" style={{ maxWidth: 900 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <div className="modal-title">Registrar pago de aseguradora</div>
                  <div style={{ fontSize: 12, color: 'var(--silver)', marginTop: 2 }}>
                    Marca las comisiones que te pagó la aseguradora y ajusta el monto recibido por póliza.
                    Si pagaron de menos, el saldo queda pendiente de la aseguradora para seguimiento.
                  </div>
                </div>
                <button className="btn btn-icon btn-secondary" onClick={() => setModalPagoAsegMasivo(false)}><X size={16} /></button>
              </div>

              <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'flex-end', padding:'10px 2px 14px' }}>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">Fecha en que pagó la aseguradora *</label>
                  <input type="date" className="form-input" style={{ width:170 }} value={pagoAsegFecha} onChange={e => setPagoAsegFecha(e.target.value)} />
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">N° de comprobante / remesa (general)</label>
                  <input className="form-input" style={{ width:200 }} value={pagoAsegComp} onChange={e => setPagoAsegComp(e.target.value)} placeholder="opcional" />
                </div>
                <div style={{ marginLeft:'auto', textAlign:'right', fontSize:12, color:'var(--silver)' }}>
                  <div>Seleccionadas: <b style={{ color:'var(--text)' }}>{marcados.length}</b> de {items.length}</div>
                  <div>Esperado: <b style={{ color:'var(--sky-light)' }}>{fmt$(totalEsperado)}</b> · Recibido: <b style={{ color:'var(--text-success)' }}>{fmt$(totalRecibido)}</b></div>
                  {Math.abs(dif) > 0.005 && (
                    <div style={{ color:'var(--text-warning)' }}>Quedará pendiente: <b>{fmt$(dif)}</b></div>
                  )}
                </div>
              </div>

              <div className="table-wrapper" style={{ maxHeight: 380, overflowY:'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width:34 }}>
                        <input type="checkbox" checked={todos} onChange={e => {
                          const v = e.target.checked;
                          setPagoAsegSel(p => { const n = { ...p }; items.forEach(r => { n[r.id] = { ...(n[r.id] || {}), sel: v }; }); return n; });
                        }} />
                      </th>
                      <th>Póliza</th><th>Cliente</th><th>Agente</th>
                      <th style={{ textAlign:'right' }}>Esperado</th>
                      <th style={{ textAlign:'right' }}>Recibido</th>
                      <th style={{ textAlign:'right' }}>Saldo</th>
                      <th title="Solo si la aseguradora da un comprobante distinto por póliza. Si lo dejas vacío se usa el número general de arriba.">Comprobante</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(r => {
                      const esperado = saldoAsegDe(r);
                      const rec = Number(pagoAsegSel[r.id]?.recibido) || 0;
                      const saldo = r2(esperado - rec);
                      const marcado = !!pagoAsegSel[r.id]?.sel;
                      return (
                        <tr key={r.id} style={{ opacity: marcado ? 1 : 0.5 }}>
                          <td><input type="checkbox" checked={marcado} onChange={e => setSel(r.id, e.target.checked)} /></td>
                          <td style={{ fontSize:12, color:'var(--sky-light)', fontWeight:600 }}>{r.polizas?.numero || '—'}</td>
                          <td style={{ fontSize:11 }}>{r.polizas?.clientes?.nombre || r.cliente || '—'}</td>
                          <td style={{ fontSize:11 }}>{r.agentes?.nombre || (r.referido_id ? 'Referido' : 'Sin agente')}</td>
                          <td style={{ textAlign:'right', fontSize:12 }}>{fmt$(esperado)}</td>
                          <td style={{ textAlign:'right' }}>
                            <input type="number" step="0.01" className="form-input" disabled={!marcado}
                              style={{ width:100, padding:'3px 6px', height:26, fontSize:12, textAlign:'right' }}
                              value={pagoAsegSel[r.id]?.recibido ?? ''} onChange={e => setMonto(r.id, e.target.value)} />
                          </td>
                          <td style={{ textAlign:'right', fontSize:12, fontWeight:700,
                            color: Math.abs(saldo) < 0.005 ? 'var(--text-success)' : 'var(--text-warning)' }}>
                            {marcado ? fmt$(saldo) : '—'}
                          </td>
                          <td>
                            <input className="form-input" disabled={!marcado}
                              style={{ width:120, padding:'3px 6px', height:26, fontSize:12 }}
                              placeholder={pagoAsegComp || 'general'}
                              value={pagoAsegSel[r.id]?.comp ?? ''}
                              onChange={e => setPagoAsegSel(p => ({ ...p, [r.id]: { ...(p[r.id] || {}), comp: e.target.value } }))} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="modal-footer" style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:14 }}>
                <button className="btn btn-secondary" onClick={() => setModalPagoAsegMasivo(false)} disabled={guardandoPagoAseg}>Cancelar</button>
                <button className="btn btn-success" onClick={guardarPagoAsegMasivo} disabled={guardandoPagoAseg || !marcados.length}>
                  <CheckCircle size={14}/> {guardandoPagoAseg ? 'Guardando…' : `Registrar pago (${marcados.length})`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {modalAseg && (
        <div className="modal-overlay" onClick={()=>{ setModalAseg(null); setNotaAseg(''); }}>
          <div className="modal" style={{ maxWidth:460 }} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Registrar pago de Aseguradora</div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>
                  {modalAseg.poliza_num} · {modalAseg.agente_nombre} · {fmt$(modalAseg.monto_agente + modalAseg.monto_ru)}
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModalAseg(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{ padding:'10px 14px', background:'rgba(139, 92, 246, 0.08)', borderRadius:8, marginBottom:16, fontSize:13, color:'var(--silver)' }}>
                Confirma que la aseguradora ya le pagó a {abrev} la comisión de esta póliza.<br/>
                Esto la pondrá en estado <strong style={{ color:'var(--text-warning)' }}>"Pendiente Agente"</strong> para que puedas liquidarla.
              </div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Fecha en que {abrev} recibió *</label>
                  <input className="form-input" type="date" value={fechaAseg} onChange={e=>setFechaAseg(e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">No. Remisión / Comprobante</label>
                  <input className="form-input" value={numRemision} onChange={e=>setNumRemision(e.target.value)} placeholder="Opcional"/>
                </div>
              </div>
              <div className="form-group" style={{ marginTop:12 }}>
                <label className="form-label">Nota (opcional)</label>
                <textarea className="form-input" rows={2} value={notaAseg} onChange={e=>setNotaAseg(e.target.value)}
                  placeholder="Ej: Pago recibido vía transferencia, liquidación parcial, etc."
                  style={{ resize:'vertical', fontFamily:'inherit' }}/>
              </div>
              <div style={{ display:'flex', gap:16, marginTop:12, padding:'10px 14px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, fontSize:13 }}>
                <span style={{ color:'var(--silver)' }}>Comisión agente: <strong style={{ color:'var(--sky)' }}>{fmt$(modalAseg.monto_agente)}</strong></span>
                <span style={{ color:'var(--silver)' }}>Comisión {abrev}: <strong style={{ color:'var(--text-success)' }}>{fmt$(modalAseg.monto_ru)}</strong></span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>{ setModalAseg(null); setNotaAseg(''); }}>Cancelar</button>
              <button className="btn btn-primary" onClick={registrarPagoAseguradora} disabled={!fechaAseg}>
                <CheckCircle size={14}/> Confirmar — Aseguradora pagó a {abrev}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Liquidar comisiones al agente */}
      {modalReverso && (
        <div className="modal-overlay" onClick={() => setModalReverso(null)}>
          <div className="modal" style={{ maxWidth:460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Revertir cierre de {abrev}</div>
            </div>
            <div style={{ padding:'8px 4px 4px' }}>
              <p style={{ fontSize:13, color:'var(--silver)', lineHeight:1.6 }}>
                Vas a <b>revertir</b> el cierre del <b>{fmtFechaHora(modalReverso.cierre_ts)}</b>. Las comisiones volverán a la bolsa de "Por Liquidar".
              </p>
              <div style={{ textAlign:'center', margin:'14px 0' }}>
                <div style={{ fontSize:28, fontWeight:800, color:'var(--text-warning)' }}>{fmt$(modalReverso.total)}</div>
                <div style={{ fontSize:12, color:'var(--silver)' }}>{modalReverso.count} comisión(es)</div>
              </div>
              <p style={{ fontSize:12, color:'var(--silver)', lineHeight:1.5 }}>
                Quedará registrado quién y cuándo revirtió ({user?.nombre || '—'}). Acción solo para superusuarios.
              </p>
            </div>
            <div className="modal-footer" style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
              <button className="btn btn-secondary" onClick={() => setModalReverso(null)} disabled={revirtiendo}>Cancelar</button>
              <button className="btn btn-secondary" style={{ color:'var(--text-warning)', borderColor:'rgba(245, 158, 11, 0.5)' }} onClick={revertirCierreRU} disabled={revirtiendo}>
                ↩️ {revirtiendo ? 'Revirtiendo...' : 'Revertir cierre'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalCierreRU && (
        <div className="modal-overlay" onClick={() => setModalCierreRU(false)}>
          <div className="modal modal-lg" style={{ maxWidth:680 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Cerrar bolsa de {abrev}</div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>
                  {bolsaRU.count} comisión(es) que componen la porción de {abrev}
                </div>
              </div>
            </div>

            {/* Detalle de las comisiones de la bolsa RU */}
            <div className="table-wrapper" style={{ maxHeight:300, overflowY:'auto', margin:'8px 0' }}>
              <table>
                <thead>
                  <tr>
                    <th>Póliza</th><th>Cliente</th><th>Agente</th><th>Pago cliente</th>
                    <th style={{ textAlign:'right' }}>Monto {abrev}</th>
                  </tr>
                </thead>
                <tbody>
                  {(bolsaRU.items||[]).map(it => (
                    <tr key={it.id}>
                      <td style={{ color:'var(--sky-light)', fontWeight:600 }}>{it.poliza_num}</td>
                      <td style={{ fontSize:12 }}>{it.cliente}</td>
                      <td style={{ fontSize:12, color:'var(--silver)' }}>{it.agente}</td>
                      <td style={{ fontSize:11, color:'var(--silver)' }}>
                        {it.cobro_fecha ? `${fmtFecha(it.cobro_fecha)} · ${fmt$(it.cobro_monto)}` : '—'}
                      </td>
                      <td style={{ textAlign:'right', color:'var(--text-success)', fontWeight:700 }}>{fmt$(it.monto_ru)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'rgba(46, 196, 125, 0.08)', border:'1px solid rgba(46, 196, 125, 0.3)', borderRadius:10 }}>
              <span style={{ fontSize:14, fontWeight:700, color:'var(--silver)' }}>Total a cerrar de {abrev}:</span>
              <span style={{ fontSize:22, fontWeight:800, color:'var(--text-success)' }}>{fmt$(bolsaRU.total)}</span>
            </div>
            <div className="form-group" style={{ marginTop:12, maxWidth:240 }}>
              <label className="form-label">Fecha del cierre *</label>
              <input className="form-input" type="date" value={fechaCierreRU} onChange={e=>setFechaCierreRU(e.target.value)} />
            </div>
            <p style={{ fontSize:12, color:'var(--silver)', lineHeight:1.5, marginTop:10 }}>
              Quedará registrada como cobrada por {abrev} con la fecha que indiques arriba. Esto no afecta los pagos a los agentes.
            </p>

            <div className="modal-footer" style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
              <button className="btn btn-secondary" onClick={() => setModalCierreRU(false)} disabled={cerrandoRU}>Cancelar</button>
              <button className="btn btn-success" onClick={cerrarBolsaRU} disabled={cerrandoRU || !fechaCierreRU}>
                <Send size={14}/> {cerrandoRU ? 'Cerrando...' : `Cerrar ${fmt$(bolsaRU.total)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalLiquidar && (
        <div className="modal-overlay" onClick={() => setModalLiquidar(null)}>
          <div className="modal modal-lg" style={{ maxWidth:620 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Liquidar comisiones — {modalLiquidar.agente_nombre}</div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>
                  {modalLiquidar.items.length} comisión(es) aprobadas listas para pagar
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={() => setModalLiquidar(null)}><X size={16}/></button>
            </div>
            <div className="modal-body" style={{ maxHeight:'65vh', overflowY:'auto' }}>
              {/* Resumen */}
              <div className="table-wrapper" style={{ marginBottom:16 }}>
                <table>
                  <thead>
                    <tr><th>Póliza</th><th>Cliente</th><th>Prima</th><th>Monto Agente</th></tr>
                  </thead>
                  <tbody>
                    {modalLiquidar.items.map(c => (
                      <tr key={c.id}>
                        <td style={{ fontSize:12, color:'var(--sky)', fontWeight:700 }}>{c.poliza_num}</td>
                        <td style={{ fontSize:12 }}>{c.cliente}</td>
                        <td style={{ fontSize:12 }}>{fmt$(c.prima)}</td>
                        <td style={{ fontWeight:700, color:'var(--sky)' }}>{fmt$(c.monto_agente)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding:'10px 14px', background:'rgba(34, 197, 94, 0.08)', border:'1px solid rgba(34, 197, 94, 0.2)', borderRadius:8, marginBottom:16, fontSize:14 }}>
                Total a pagar al agente: <strong style={{ color:'var(--text-success)', fontSize:18 }}>
                  {fmt$(modalLiquidar.items.reduce((s,c) => s + Number(c.monto_agente||0), 0))}
                </strong>
              </div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Fecha de transferencia *</label>
                  <input className="form-input" type="date" value={fechaTransf} onChange={e=>setFechaTransf(e.target.value)}/>
                </div>
                <div className="form-group">
                  <label className="form-label">No. de transferencia *</label>
                  <input className="form-input" value={numTransf} onChange={e=>setNumTransf(e.target.value)}
                    placeholder="Ej: 202505310001"/>
                </div>
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Nota (opcional)</label>
                  <textarea className="form-textarea" rows={2} value={notaLiquidacion}
                    onChange={e=>setNotaLiquidacion(e.target.value)}
                    placeholder="Nota libre de quien liquida (ej: pago parcial, ajuste, observación...)"/>
                </div>
              </div>
              <div style={{ fontSize:12, color:'var(--silver)', marginTop:8 }}>
                Se guardará como nota en cada comisión: <em>"Transferencia #{numTransf || '...'} · {fechaTransf || 'fecha'}{notaLiquidacion.trim() ? ' · ' + notaLiquidacion.trim() : ''}"</em>
              </div>
            </div>
            <div className="modal-footer" style={{ flexWrap:'wrap' }}>
              <button className="btn btn-secondary" onClick={() => setModalLiquidar(null)}>Cancelar</button>
              <button className="btn btn-secondary"
                onClick={() => imprimirLiquidacion(modalLiquidar.items, modalLiquidar.agente_nombre, fechaTransf, numTransf)}
                title="Genera PDF del detalle">
                🖨️ Vista previa PDF
              </button>
              <button className="btn btn-secondary"
                onClick={() => exportarLiquidacionExcel(modalLiquidar.items, modalLiquidar.agente_nombre, fechaTransf, numTransf)}
                title="Descarga el detalle en Excel">
                📊 Exportar Excel
              </button>
              <button className="btn btn-success" onClick={confirmarLiquidacion} disabled={liquidando || !fechaTransf || !numTransf.trim()}>
                <Send size={14}/> {liquidando ? 'Procesando...' : `Confirmar y pagar ${fmt$(modalLiquidar.items.reduce((s,c)=>s+Number(c.monto_agente||0),0))}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal importar estado de cuenta (ASSA) */}
      {modalEstadoCuenta && (
        <ImportarEstadoCuenta onClose={() => setModalEstadoCuenta(false)} />
      )}

      {/* Modal detalle de comisión */}
      {detalle && (
        <div className="modal-overlay" onClick={() => setDetalle(null)}>
          <div className="modal modal-lg" style={{ maxWidth:820, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Detalle de Comisión — {detalle.poliza_num}</div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>
                  {detalle.cliente} · {detalle.agente_nombre}
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={() => setDetalle(null)}><X size={16}/></button>
            </div>

            <div className="modal-body" style={{ flex:1, minHeight:0, overflowY:'auto' }}>
              {loadingDetalle ? (
                <div style={{ textAlign:'center', padding:40, color:'var(--silver)' }}>Cargando...</div>
              ) : detalleData && (
                <>
                  {/* Aviso de anulación */}
                  {detalle.estado === 'anulada' && (
                    <div style={{ marginBottom:16, padding:'10px 14px', background:'rgba(239, 68, 68, 0.08)', border:'1px solid rgba(239, 68, 68, 0.3)', borderRadius:8 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:'var(--text-danger)', letterSpacing:1, marginBottom:4 }}>COMISIÓN ANULADA — NO CUENTA COMO INGRESO</div>
                      <div style={{ fontSize:12, color:'#e2e8f0' }}>{detalle.motivo_anulacion || '—'}</div>
                      <div style={{ fontSize:11, color:'var(--silver)', marginTop:4 }}>
                        Anulada por {detalle.anulado_por || '—'}{detalle.anulado_en ? ` · ${fmtFechaHora(detalle.anulado_en)}` : ''}
                      </div>
                    </div>
                  )}
                  {/* Datos del pago que generó esta comisión */}
                  <div style={{ marginBottom:20 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1 }}>ESTA COMISIÓN</div>
                      {canEdit('comisiones') && !esAgenteRestringido && detalle.estado !== 'anulada' && (
                        <div style={{ display:'flex', gap:6 }}>
                          {detalle.fecha_recibio_comision && (
                            <button className="btn btn-sm btn-secondary" title="Corregir la fecha o el comprobante del pago de la aseguradora (el monto no cambia)"
                              onClick={() => abrirCorregirPagoAseg(detalle)}>
                              <Edit2 size={12}/> Corregir pago de aseguradora
                            </button>
                          )}
                          {esReversibleAseg(detalle) && (
                            <button className="btn btn-sm btn-secondary" style={{ color:'var(--text-warning)', borderColor:'rgba(245, 158, 11, 0.4)' }}
                              title='Deshacer el pago de la aseguradora: la comisión vuelve a "Esp. Aseguradora"'
                              onClick={() => abrirRevertirPagoAseg(detalle)}>
                              <RefreshCw size={12}/> Revertir pago de aseguradora
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
                      {[
                        ['Prima del pago', fmt$(detalle.prima)],
                        ['Comisión agente', fmt$(detalle.monto_agente)],
                        [`Comisión ${abrev}`, fmt$(detalle.monto_ru)],
                        ['Estado', detalle.estado],
                        ['Tipo', detalle.tipo],
                        ['Comprobante', detalle.num_comprobante || '—'],
                        ['Fecha pago cliente', fmtFecha(detalle.fecha_pago_cliente || detalle.pago_info?.fecha || detalle.fecha_recibio_comision) || '—'],
                        [`Aseg. pagó a ${abrev}`, fmtFecha(detalle.fecha_recibio_comision) || '—'],
                        ['Agente cobrado', detalle.pagado_en ? fmtFecha(detalle.pagado_en) : (fmtFecha(detalle.fecha_liquidacion) || '—')],
                      ].map(([l, v]) => (
                        <div key={l} style={{ padding:'8px 12px', background:'rgba(var(--steel-rgb),0.35)', borderRadius:8 }}>
                          <div style={{ fontSize:10, color:'var(--silver)', textTransform:'uppercase', marginBottom:2 }}>{l}</div>
                          <div style={{ fontSize:13, fontWeight:600 }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {detalle.pago_info && (
                      <div style={{ marginTop:10, padding:'8px 14px', background:'rgba(var(--sky-rgb),0.08)', borderRadius:8, fontSize:12, display:'flex', gap:20 }}>
                        <span><span style={{ color:'var(--silver)' }}>Fecha cobro: </span><strong>{fmtFecha(detalle.pago_info.fecha || detalle.pago_info.fecha_pago) || '—'}</strong></span>
                        <span><span style={{ color:'var(--silver)' }}>Monto cobro: </span><strong>{fmt$(detalle.pago_info.monto)}</strong></span>
                        <span><span style={{ color:'var(--silver)' }}>Dónde: </span><strong>{detalle.pago_info.donde_pago || '—'}</strong></span>
                        <span><span style={{ color:'var(--silver)' }}>Forma: </span><strong>{detalle.pago_info.forma_pago || detalle.pago_info.forma_pago_real || '—'}</strong></span>
                        {detalle.pago_info_tipo === 'pago' && detalle.pago_info.cuota_num && (
                          <span><span style={{ color:'var(--silver)' }}>Cuota: </span><strong>#{detalle.pago_info.cuota_num}</strong></span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Nota del cobro (ej. traza del estado de cuenta) y/o nota de la comisión */}
                  {detalle.pago_info?.notas && (
                    <div style={{ marginBottom:12, padding:'10px 14px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:'var(--sky-light)', letterSpacing:1, marginBottom:4 }}>NOTA DEL COBRO</div>
                      <div style={{ fontSize:12, color:'#e2e8f0' }}>{detalle.pago_info.notas}</div>
                    </div>
                  )}
                  {detalle?.notas && (
                    <div style={{ marginBottom:20, padding:'10px 14px', background:'rgba(139, 92, 246, 0.08)', border:'1px solid rgba(139, 92, 246, 0.2)', borderRadius:8 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:'var(--text-purple)', letterSpacing:1, marginBottom:4 }}>NOTA DE LA COMISIÓN</div>
                      <div style={{ fontSize:12, color:'#e2e8f0' }}>{detalle.notas}</div>
                    </div>
                  )}

                  {/* Todos los pagos del cliente en esta póliza */}
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:10 }}>
                      PAGOS DEL CLIENTE — PÓLIZA {detalle.poliza_num}
                    </div>
                    <div className="table-wrapper">
                      <table>
                        <thead>
                          <tr><th>Cuota</th><th>Vencimiento</th><th>Monto</th><th>Estado</th><th>Fecha Pago</th><th>Dónde</th><th>Forma</th></tr>
                        </thead>
                        <tbody>
                          {detalleData.pagos.length === 0
                            ? <tr><td colSpan={7} style={{ textAlign:'center', color:'var(--silver)', padding:12 }}>Sin pagos registrados</td></tr>
                            : detalleData.pagos.map((p, i) => (
                              <tr key={i} style={p.estado === 'pagado' ? {} : { opacity:0.6 }}>
                                <td style={{ fontSize:12, color:'var(--silver)' }}>#{p.cuota_num || '—'}</td>
                                <td style={{ fontSize:12 }}>{fmtFecha(p.fecha_vencimiento) || '—'}</td>
                                <td style={{ fontWeight:600 }}>{fmt$(p.monto)}</td>
                                <td><span className={`badge ${p.estado === 'pagado' ? 'badge-success' : 'badge-warning'}`}>{p.estado}</span></td>
                                <td style={{ fontSize:12, color:'var(--silver)' }}>{fmtFecha(p.fecha_pago) || '—'}</td>
                                <td style={{ fontSize:12, color:'var(--silver)' }}>{p.donde_pago || '—'}</td>
                                <td style={{ fontSize:12, color:'var(--silver)' }}>{p.forma_pago_real || '—'}</td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Historial de comisiones liquidadas al agente para esta póliza */}
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:10 }}>
                      HISTORIAL DE LIQUIDACIONES AL AGENTE — ESTA PÓLIZA
                    </div>
                    {detalleData.histCom.filter(c => c.agente_id === detalle.agente_id).length === 0
                      ? <div style={{ fontSize:12, color:'var(--silver)' }}>Sin historial de liquidaciones.</div>
                      : (
                        <>
                          <div className="table-wrapper">
                            <table>
                              <thead>
                                <tr><th>Pagó cliente</th><th>Prima</th><th>Monto Agente</th><th>Monto {abrev}</th><th>Comprobante</th><th>Aseg. → {abrev}</th><th>Agente cobrado</th><th>Estado</th><th>Nota</th></tr>
                              </thead>
                              <tbody>
                                {detalleData.histCom.filter(c => c.agente_id === detalle.agente_id).map((c, i) => (
                                  <tr key={i} style={c.estado === 'anulada' ? { opacity:0.5, textDecoration:'line-through' } : {}}>
                                    <td style={{ fontSize:11, color: c._fecha_pago_cliente ? 'var(--sky-light)' : 'var(--silver)', fontWeight:600 }}>{fmtFecha(c._fecha_pago_cliente) || '—'}</td>
                                    <td style={{ fontSize:12 }}>{fmt$(c.prima)}</td>
                                    <td style={{ fontWeight:700, color:'var(--sky)' }}>{fmt$(c.monto_agente)}</td>
                                    <td style={{ fontWeight:700, color:'var(--text-success)' }}>{fmt$(c.monto_ru)}</td>
                                    <td style={{ fontSize:11, color:'var(--silver)' }}>{c.num_comprobante || '—'}</td>
                                    <td style={{ fontSize:11, color: c.fecha_recibio_comision ? 'var(--sky-light)' : 'var(--silver)' }}>
                                      {fmtFecha(c.fecha_recibio_comision) || '—'}
                                    </td>
                                    <td style={{ fontSize:11, color: c.pagado_en ? '#22c55e' : 'var(--silver)' }}>
                                      {c.pagado_en ? fmtFecha(c.pagado_en) : '—'}
                                    </td>
                                    <td><span className={`badge ${c.estado === 'pagado' ? 'badge-success' : c.estado === 'aprobado' ? 'badge-info' : 'badge-warning'}`}>{c.estado}</span></td>
                                    <td style={{ fontSize:11, color:'var(--silver)', maxWidth:180, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} title={c.notas || ''}>{c.notas || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div style={{ marginTop:10, fontSize:13, color:'var(--silver)' }}>
                            Total efectivamente pagado al agente: <strong style={{ color:'var(--sky)' }}>{fmt$(totalAgentePorPoliza)}</strong>
                          </div>
                        </>
                      )
                    }
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              {detalle && canEdit('comisiones') && detalle.estado !== 'anulada' && (
                <button className="btn btn-secondary" style={{ marginRight:'auto', color:'var(--text-danger)', borderColor:'rgba(239, 68, 68, 0.4)' }}
                  onClick={() => abrirAnular(detalle)}>
                  <X size={14}/> Anular comisión
                </button>
              )}
              {detalle && canEdit('comisiones') && detalle.estado === 'anulada' && (
                <button className="btn btn-secondary" style={{ marginRight:'auto' }}
                  onClick={() => revertirAnulacion(detalle)}>
                  <RefreshCw size={14}/> Revertir anulación
                </button>
              )}
              {detalle && canEdit('comisiones') && detalle.estado !== 'anulada' && (
                <>
                  {detalle.estado === 'pendiente_aseg' && (
                    <button className="btn btn-secondary" style={{ color:'var(--text-purple)', borderColor:'rgba(139, 92, 246, 0.4)' }}
                      onClick={() => { setDetalle(null); setModalAseg(detalle); setFechaAseg(new Date().toISOString().slice(0,10)); setNumRemision(''); }}>
                      <Building size={14}/> Aseg. pagó a {abrev}
                    </button>
                  )}
                  {detalle.estado === 'pendiente' && (
                    <button className="btn btn-secondary" onClick={() => { aprobar(detalle); setDetalle(null); }}>
                      <CheckCircle size={14}/> Aprobar
                    </button>
                  )}
                  {detalle.estado === 'aprobado' && (
                    <button className="btn btn-success" onClick={() => { marcarPagado(detalle); setDetalle(null); }}>
                      <CheckCircle size={14}/> Marcar Pagado al Agente
                    </button>
                  )}
                </>
              )}
              <button className="btn btn-secondary" onClick={() => setDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: anular comisión (duplicada o error de BD) */}
      {/* Modal revertir pago de aseguradora (vuelve a "Esp. Aseguradora") */}
      {modalRevertirAseg && (
        <div className="modal-overlay" style={{ zIndex:1500 }} onClick={() => { if (!revirtiendoAseg) setModalRevertirAseg(null); }}>
          <div className="modal" style={{ maxWidth:520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Revertir pago de aseguradora</div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>
                  Póliza {modalRevertirAseg.poliza_num || modalRevertirAseg.polizas?.numero || '—'} · {modalRevertirAseg.cliente || ''}
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={() => setModalRevertirAseg(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{ padding:'10px 12px', background:'rgba(245, 158, 11, 0.08)', border:'1px solid rgba(245, 158, 11, 0.3)', borderRadius:8, marginBottom:16, fontSize:12 }}>
                <div style={{ fontWeight:700, color:'var(--text-warning)', marginBottom:4 }}>La comisión vuelve a "Esp. Aseguradora".</div>
                <div style={{ color:'var(--text)' }}>
                  Se borra el pago registrado: fecha <strong>{fmtFecha(modalRevertirAseg.fecha_recibio_comision) || '—'}</strong>,
                  comprobante <strong>{modalRevertirAseg.num_comprobante || '—'}</strong>,
                  recibido <strong>{fmt$(modalRevertirAseg.monto_recibido_aseg || 0)}</strong>.
                  La comisión no se elimina ni cambia de monto; queda esperando el pago real de la aseguradora y el detalle guarda lo que tenía.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Motivo (opcional)</label>
                <input className="form-input" value={revertirForm.nota} onChange={e => setRevertirForm(f => ({ ...f, nota: e.target.value }))} placeholder="Ej: la aseguradora no incluyó esta póliza en el pago"/>
              </div>
              {revertirLote.length > 0 && (
                <label style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'10px 12px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8, fontSize:12, cursor:'pointer' }}>
                  <input type="checkbox" checked={revertirForm.lote} onChange={e => setRevertirForm(f => ({ ...f, lote: e.target.checked }))} style={{ marginTop:2 }}/>
                  <span>
                    <strong>Revertir también las otras {revertirLote.length} comisión(es) del mismo pago</strong>
                    <span style={{ color:'var(--silver)' }}> (misma fecha {fmtFecha(modalRevertirAseg.fecha_recibio_comision)} y comprobante {modalRevertirAseg.num_comprobante}, aún sin aprobar): </span>
                    {revertirLote.slice(0, 8).map(r => r.polizas?.numero || r.id).join(', ')}{revertirLote.length > 8 ? '...' : ''}
                  </span>
                </label>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalRevertirAseg(null)} disabled={revirtiendoAseg}>Cancelar</button>
              <button className="btn btn-primary" onClick={confirmarRevertirPagoAseg} disabled={revirtiendoAseg}>
                <RefreshCw size={14}/> {revirtiendoAseg ? 'Revirtiendo...' : (revertirForm.lote && revertirLote.length ? `Revertir ${revertirLote.length + 1} comisiones` : 'Revertir pago')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal corregir pago de aseguradora (fecha / comprobante) — solo master */}
      {modalCorregirAseg && (
        <div className="modal-overlay" style={{ zIndex:1500 }} onClick={() => { if (!corrigiendo) setModalCorregirAseg(null); }}>
          <div className="modal" style={{ maxWidth:520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Corregir pago de aseguradora</div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>
                  Póliza {modalCorregirAseg.poliza_num || modalCorregirAseg.polizas?.numero || '—'} · recibido {fmt$(modalCorregirAseg.monto_recibido_aseg || (Number(modalCorregirAseg.monto_agente||0) + Number(modalCorregirAseg.monto_ru||0)))}
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={() => setModalCorregirAseg(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{ padding:'8px 12px', background:'rgba(245, 158, 11, 0.08)', borderRadius:8, marginBottom:16, fontSize:12, color:'var(--text-warning)' }}>
                Solo se corrige la fecha en que pagó la aseguradora y el comprobante. El monto recibido y el estado de la comisión no cambian; la corrección queda anotada en la comisión.
              </div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Fecha en que pagó la aseguradora *</label>
                  <input className="form-input" type="date" value={corregirForm.fecha} onChange={e => setCorregirForm(f => ({ ...f, fecha: e.target.value }))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Comprobante</label>
                  <input className="form-input" value={corregirForm.comprobante} onChange={e => setCorregirForm(f => ({ ...f, comprobante: e.target.value }))} placeholder="N° de remisión / transferencia"/>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Motivo (opcional)</label>
                <input className="form-input" value={corregirForm.nota} onChange={e => setCorregirForm(f => ({ ...f, nota: e.target.value }))} placeholder="Ej: se registró con la fecha del correo, no la del pago"/>
              </div>
              {corregirLote.length > 0 && (
                <label style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'10px 12px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8, fontSize:12, cursor:'pointer' }}>
                  <input type="checkbox" checked={corregirForm.lote} onChange={e => setCorregirForm(f => ({ ...f, lote: e.target.checked }))} style={{ marginTop:2 }}/>
                  <span>
                    <strong>Aplicar también a las otras {corregirLote.length} comisión(es) del mismo pago</strong>
                    <span style={{ color:'var(--silver)' }}> (misma fecha {fmtFecha(modalCorregirAseg.fecha_recibio_comision)} y comprobante {modalCorregirAseg.num_comprobante}): </span>
                    {corregirLote.slice(0, 8).map(r => r.polizas?.numero || r.id).join(', ')}{corregirLote.length > 8 ? '...' : ''}
                  </span>
                </label>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalCorregirAseg(null)} disabled={corrigiendo}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarCorreccionAseg} disabled={corrigiendo || !corregirForm.fecha}>
                <CheckCircle size={14}/> {corrigiendo ? 'Guardando...' : (corregirForm.lote && corregirLote.length ? `Corregir ${corregirLote.length + 1} comisiones` : 'Guardar corrección')}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalAnular && (
        <div className="modal-overlay" style={{ zIndex:1400 }} onClick={() => { if (!anulando) setModalAnular(null); }}>
          <div className="modal" style={{ maxWidth:560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title" style={{ color:'var(--text-danger)' }}>Anular comisión</div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>
                  {modalAnular.poliza_num} · {modalAnular.referido_id ? `Referido: ${modalAnular.referido_nombre}` : modalAnular.agente_nombre} · {fmt$((Number(modalAnular.monto_agente)||0)+(Number(modalAnular.monto_ru)||0))}
                </div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={() => { if (!anulando) setModalAnular(null); }}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize:12, color:'var(--silver)', marginBottom:14, padding:'8px 12px', background:'rgba(239, 68, 68, 0.06)', borderRadius:8, border:'1px solid rgba(239, 68, 68, 0.2)' }}>
                La comisión NO se borra: queda marcada como <strong style={{color:'var(--text-danger)'}}>anulada</strong> con el motivo, deja de contar como ingreso y se excluye de todos los totales. Se puede revertir.
              </div>

              <div style={{ fontSize:11, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:8 }}>MOTIVO</div>
              <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                {[['duplicada','Es duplicada'],['error','Error de BD / no debió existir']].map(([val,lbl]) => (
                  <button key={val} onClick={() => setAnularTipo(val)}
                    style={{ flex:1, padding:'10px 12px', borderRadius:8, cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600,
                      border: anularTipo===val ? '1px solid var(--sky)' : '1px solid rgba(var(--silver-rgb),0.25)',
                      background: anularTipo===val ? 'rgba(var(--sky-rgb),0.15)' : 'transparent',
                      color: anularTipo===val ? 'var(--sky-light)' : 'var(--silver)' }}>
                    {lbl}
                  </button>
                ))}
              </div>

              {anularTipo === 'duplicada' && (
                <div style={{ marginBottom:14 }}>
                  <label className="form-label">Comisión original (la que se queda) *</label>
                  <select className="form-select" value={anularDupId} onChange={e => setAnularDupId(e.target.value)}>
                    <option value="">Seleccionar la comisión correcta...</option>
                    {(detalleData?.histCom || [])
                      .filter(c => String(c.id) !== String(modalAnular.id) && c.estado !== 'anulada')
                      .map(c => (
                        <option key={c.id} value={c.id}>
                          Pagó cliente: {fmtFecha(c._fecha_pago_cliente) || 's/f'} · {c.agentes?.nombre || (c.referido_id ? 'Referido' : 'Sin agente')} · {ESTADO_INFO[c.estado]?.label || c.estado} · Ag {fmt$(c.monto_agente)} / {abrev} {fmt$(c.monto_ru)} · #{c.id}
                        </option>
                      ))}
                  </select>
                  <div style={{ fontSize:11, color:'var(--silver)', marginTop:6 }}>
                    Si la duplicada que anulas está más avanzada que la original, su gestión (aseg. pagó, aprobado, pagado, comprobante) se copia a la original para no perderla.
                  </div>
                </div>
              )}

              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">{anularTipo === 'error' ? 'Motivo / comentario *' : 'Comentario (opcional)'}</label>
                <textarea className="form-textarea" value={anularTexto} onChange={e => setAnularTexto(e.target.value)}
                  placeholder={anularTipo === 'error' ? '¿Por qué se elimina esta comisión?' : 'Detalle adicional...'} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { if (!anulando) setModalAnular(null); }} disabled={anulando}>Cancelar</button>
              <button className="btn btn-primary" style={{ background:'#ef4444', bordercolor:'var(--text-danger)' }} onClick={confirmarAnular} disabled={anulando}>
                {anulando ? 'Anulando...' : 'Anular comisión'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
